import { openSupportChat } from "../../services/supportChat";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import FeatureAccessCell from "../../components/Addons/FeatureAccessCell";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Checkbox, IconButton, Box, Chip, Popover, Paper, List, ListItem, ListItemButton, Tooltip } from "@mui/material";
import Toggle from "../../components/DataInput/Toggle";
import AddLicensesDialog from "../../components/Billing/AddLicensesDialog";
import { useTheme } from "../../common/contexts/ThemeContext";

import { GAPS, COLORS, RADII, FONTS, FONT_WEIGHTS, FONT_FAMILIES, SPACINGS, SIZES, getColors } from "../../common/consts";
import { devLog, devWarn } from "../../common/utils/devLog";
import { createRowOrderState, orderUsersStably } from "../../common/utils/userOrdering";
import Card from "../../components/Display/Card";
import Grid from "../../components/Display/Grid";
import Text, { TextVariant } from "../../components/Display/Text";
import DataGrid, {
  DataGridCol,
  DataGridRow,
} from "../../components/Display/DataGrid";
import Button, { ButtonVariants } from "../../components/Display/Button";
import Input from "../../components/DataInput/Input";
import Select from "../../components/DataInput/Select";
import Tabs from "../../components/Display/Tabs";
import {
  OutreachUser,
  OutreachDisplayUser,
  HubSpotUser,
  HubSpotDisplayUser,
} from "../../types/users";
import { ProfileType } from "../../types/users";
import { getOutreachUsers, getHubSpotUsers, getOutreachAuthUrl, updateOrganizationSettings, getMyOrganization, updateOrganizationAllowedEmails, convertAppOrganizationSettingsToDbOrganizationSettings, convertDbOrganizationSettingsToAppOrganizationSettings } from "../../services/firebase";
import { removeUndefinedAndNull } from "../../common/utils/data";
import { hubspotUsersSearchAtom, organizationAtom, signedInUserAtom } from "../../state/atoms";
import { ReactComponent as RefreshIcon } from "../../assets/icons/refresh.svg";
import { toast } from "react-toastify";
import Dialog from "../../components/Display/Dialog";
import { useIsHubSpotCrm } from "../../components/CrmUi/useCrm";
import HubSpotUsersPage, { UsersFilterType } from "./HubSpotUsersPage";
import { OuSearch } from "../../components/HubSpotUi";

// Helper function to get user initials
const getUserInitials = (fullName: string): string => {
  const parts = fullName.trim().split(" ");
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return fullName.substring(0, 2).toUpperCase();
};

const getOutreachUsersColumns = (
  selectedUsers: Set<string>,
  setSelectedUsers: React.Dispatch<React.SetStateAction<Set<string>>>,
  filteredAndSortedUsers: DataGridRow<OutreachDisplayUser>[],
  onToggleLicense: (userId: string, email: string, checked: boolean) => Promise<void>,
  availableLicenses: number | null,
  activeBoringUsersCount: number,
  isHubSpotIntegration = false,
  savingLicenseIds: Set<string> = new Set()
): DataGridCol<OutreachDisplayUser>[] => [
  {
    field: "checkbox",
    headerName: "",
    width: 50,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const userId = params.row.id.toString();
      const isSelected = selectedUsers.has(userId);
      
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(e) => {
              const newSelected = new Set(selectedUsers);
              if (e.target.checked) {
                newSelected.add(userId);
              } else {
                newSelected.delete(userId);
              }
              setSelectedUsers(newSelected);
            }}
            sx={{
              color: "var(--color-borderDark, #e0e0e0)",
              "&.Mui-checked": {
                color: "var(--color-foregroundBrand, #5e5eaf)",
              },
              "&:focus": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&:focus-visible": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&.Mui-focusVisible": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&.Mui-focused": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&:active": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "& .MuiSvgIcon-root": {
                "&:focus": {
                  outline: "none !important",
                  boxShadow: "none !important",
                },
              },
              "& .MuiTouchRipple-root": {
                display: "none !important",
              },
            }}
          />
        </div>
      );
    },
    renderHeader: () => {
      const allSelected = filteredAndSortedUsers.length > 0 && 
        filteredAndSortedUsers.every(user => selectedUsers.has(user.id.toString()));
      const someSelected = filteredAndSortedUsers.some(user => selectedUsers.has(user.id.toString()));
      
      return (
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          height: "100%",
          padding: "16px 0",
          width: "100%",
        }}>
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={(e) => {
              if (e.target.checked) {
                const allIds = new Set(filteredAndSortedUsers.map(user => user.id.toString()));
                setSelectedUsers(allIds);
              } else {
                setSelectedUsers(new Set());
              }
            }}
            sx={{
              color: "var(--color-borderDark, #e0e0e0)",
              "&.Mui-checked": {
                color: "var(--color-foregroundBrand, #5e5eaf)",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "var(--color-foregroundBrand, #5e5eaf)",
              },
              "&:focus": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&:focus-visible": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&.Mui-focusVisible": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&.Mui-focused": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "&:active": {
                outline: "none !important",
                boxShadow: "none !important",
              },
              "& .MuiTouchRipple-root": {
                display: "none !important",
              },
            }}
          />
        </div>
      );
    },
  },
  {
    field: "fullName",
    headerName: "Name",
    flex: 1.3,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const user = params.row;
      const initials = getUserInitials(user.fullName);
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            height: "100%",
            padding: "8px 24px 8px 0",
            minWidth: 0,
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#56557B",
              flexShrink: 0,
            }}
          >
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.fullName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <span
                style={{
                  color: "#FAFAFB",
                  fontSize: "12px",
                  fontWeight: FONT_WEIGHTS.medium,
                  fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                }}
              >
                {initials}
              </span>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontWeight: FONT_WEIGHTS.medium,
                fontSize: "14px",
                color: "var(--color-foregroundPrimary, #454262)",
                lineHeight: "1.4",
                whiteSpace: "nowrap",
                overflow: "visible",
              }}
            >
              {user.fullName}
            </div>
            <div
              style={{
                fontSize: "13px",
                color: "var(--color-foregroundSecondary, #686781)",
                lineHeight: "1.4",
                whiteSpace: "nowrap",
                overflow: "visible",
              }}
            >
              {user.email}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    field: "teams",
    headerName: "Teams",
    flex: 1.5,
    minWidth: 200,
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const teams = params.row.teams;
      const teamsText = teams && teams.length > 0 ? teams.join(", ") : "No teams assigned";
      return (
        <div 
          style={{ 
            display: "flex", 
            alignItems: "center", 
            height: "100%", 
            width: "100%",
            paddingRight: "16px",
          }}
          title={teamsText} // Tooltip for full text on hover
        >
          <span
            style={{
              fontSize: "14px",
              color: teams && teams.length > 0 ? COLORS.foregroundPrimary : COLORS.foregroundSecondary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {teamsText}
          </span>
        </div>
      );
    },
  },
  {
    field: "role",
    headerName: "Profile",
    flex: 0.8,
    minWidth: 120,
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const profile = params.row.role;
      const profileText = profile || "Default";
      return (
        <div 
          style={{ 
            display: "flex", 
            alignItems: "center", 
            height: "100%", 
            width: "100%",
            paddingRight: "16px",
          }}
          title={profileText}
        >
          <span
            style={{
              fontSize: "14px",
              color: COLORS.foregroundPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {profileText}
          </span>
        </div>
      );
    },
  },
  {
    field: "featureAccess",
    headerName: "Product access",
    flex: 1,
    minWidth: 190,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => (
      <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
        <FeatureAccessCell email={params.row.email} licensed={!!params.row.isWhitelisted} />
      </div>
    ),
  },
  {
    field: "isWhitelisted",
    headerName: "Boring license",
    flex: 0.7,
    minWidth: 120,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const user = params.row;
      const isWhitelisted = user.isWhitelisted;
      const userId = user.id.toString();
      
      // Check if we can turn on the license (if it's currently off)
      const canTurnOn = isWhitelisted || availableLicenses === null || (availableLicenses - activeBoringUsersCount) > 0;
      const isSaving = savingLicenseIds.has(userId);
      const isDisabled = (!canTurnOn && !isWhitelisted) || isSaving;
      
      return (
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          height: "100%", 
          width: "100%",
          padding: "0",
          margin: "0",
        }}>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Toggle
              checked={isWhitelisted}
              onChange={(checked) => {
                onToggleLicense(userId, user.email, checked);
              }}
              disabled={isDisabled}
              saving={isSaving}
            />
          </div>
        </div>
      );
    },
  },
  {
    field: "isActive",
    headerName: isHubSpotIntegration ? "HubSpot user status" : "Outreach user status",
    flex: 0.7,
    minWidth: 140,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams<OutreachDisplayUser>) => {
      const isActive = params.row.isActive;
      return (
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center", 
          height: "100%", 
          width: "100%",
          padding: "0",
          margin: "0",
        }}>
          {isActive ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="User is unlocked"
            >
              <path
                d="M12 14V16"
                stroke={COLORS.foregroundSecondary}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12 13C12.5523 13 13 13.4477 13 14V16C13 16.5523 12.5523 17 12 17C11.4477 17 11 16.5523 11 16V14C11 13.4477 11.4477 13 12 13Z"
                fill={COLORS.foregroundSecondary}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9 7C9 6.49336 9.16022 5.71113 9.61153 5.0843C10.0261 4.50847 10.7259 4 12 4C13.0308 4 13.6594 4.30128 14.081 4.68079C14.5262 5.08142 14.8291 5.64948 15.0513 6.31623C15.226 6.84017 15.7923 7.12333 16.3162 6.94868C16.8402 6.77404 17.1233 6.20772 16.9487 5.68377C16.6709 4.85052 16.2238 3.91858 15.419 3.19421C14.5906 2.44872 13.4692 2 12 2C10.0741 2 8.77386 2.82487 7.98847 3.9157C7.23978 4.95554 7 6.17331 7 7V9H6.5C5.11929 9 4 10.1193 4 11.5V18.5C4 19.8807 5.11929 21 6.5 21H17.5C18.8807 21 20 19.8807 20 18.5V11.5C20 10.1193 18.8807 9 17.5 9H9V7ZM6 11.5C6 11.2239 6.22386 11 6.5 11H17.5C17.7761 11 18 11.2239 18 11.5V18.5C18 18.7761 17.7761 19 17.5 19H6.5C6.22386 19 6 18.7761 6 18.5V11.5Z"
                fill={COLORS.foregroundSecondary}
              />
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-label="User is locked"
            >
              <path
                d="M12 14V16"
                stroke={COLORS.foregroundSecondary}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9 7C9 6.49336 9.16022 5.71113 9.61153 5.0843C10.0261 4.50847 10.7259 4 12 4C13.2741 4 13.9739 4.50847 14.3885 5.0843C14.8398 5.71113 15 6.49336 15 7V9H9V7ZM7 9V7C7 6.17331 7.23978 4.95554 7.98847 3.9157C8.77386 2.82487 10.0741 2 12 2C13.9259 2 15.2261 2.82487 16.0115 3.9157C16.7602 4.95554 17 6.17331 17 7V9H17.5C18.8807 9 20 10.1193 20 11.5V18.5C20 19.8807 18.8807 21 17.5 21H6.5C5.11929 21 4 19.8807 4 18.5V11.5C4 10.1193 5.11929 9 6.5 9H7ZM16 11H8H6.5C6.22386 11 6 11.2239 6 11.5V18.5C6 18.7761 6.22386 19 6.5 19H17.5C17.7761 19 18 18.7761 18 18.5V11.5C18 11.2239 17.7761 11 17.5 11H16Z"
                fill={COLORS.foregroundSecondary}
              />
            </svg>
          )}
        </div>
      );
    },
  },
];

const Users: FC = () => {
  const organization = useRecoilValue(organizationAtom);
  const setOrganization = useSetRecoilState(organizationAtom);
  const signedInUser = useRecoilValue(signedInUserAtom);
  const [outreachUsers, setOutreachUsers] = useState<
    DataGridRow<OutreachDisplayUser>[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // A user picked in the top bar's search arrives as this page's search.
  const [pickedUser, setPickedUser] = useRecoilState(hubspotUsersSearchAtom);
  useEffect(() => {
    if (!pickedUser) return;
    setSearchQuery(pickedUser);
    setPickedUser("");
  }, [pickedUser, setPickedUser]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const [filterSearchQuery, setFilterSearchQuery] = useState("");
  const [filterConfigAnchorEl, setFilterConfigAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedFilterType, setSelectedFilterType] = useState<string | null>(null);
  const [filterOperator, setFilterOperator] = useState<"is" | "is not">("is");
  const [filterValue, setFilterValue] = useState<string>("");
  const [filterValues, setFilterValues] = useState<string[]>([]); // For multi-select filters
  const [filters, setFilters] = useState<{
    status?: { operator: string; value: string } | null;
    boringLicense?: { operator: string; value: string } | null;
    role?: { operator: string; values: string[] } | null; // Multi-select
    teams?: { operator: string; values: string[] } | null; // Multi-select
  }>({});

  const [hasLockedField, setHasLockedField] = useState<boolean | null>(null);
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false);
  const [isAddLicensesOpen, setIsAddLicensesOpen] = useState(false);
  // False when Outreach returned users without role/team data (fallback
  // mode) — syncing from such data would wrongly revoke licenses.
  const [outreachDataComplete, setOutreachDataComplete] = useState(true);
  const [syncType, setSyncType] = useState<"team" | "role" | "both">("team");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [useSavedConfig, setUseSavedConfig] = useState(true);
  const [teamSelectValue, setTeamSelectValue] = useState<string>("");
  const [roleSelectValue, setRoleSelectValue] = useState<string>("");
  const [availableTeams, setAvailableTeams] = useState<string[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [syncConfigAnchorEl, setSyncConfigAnchorEl] = useState<HTMLDivElement | null>(null);
  const [syncConfigAnchorPosition, setSyncConfigAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [syncConfigSearchQuery, setSyncConfigSearchQuery] = useState<string>("");
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [expandedAddingUsers, setExpandedAddingUsers] = useState(false);
  const [expandedRemovingUsers, setExpandedRemovingUsers] = useState(false);

  const whitelistedEmails = useMemo(
    () =>
      new Set(
        (organization?.allowedOwnerEmails || []).map((email) =>
          email.toLowerCase().trim()
        )
      ),
    [organization?.allowedOwnerEmails]
  );

  const activeBoringUsersCount = useMemo(() => {
    // Count ALL users who have licenses (including inactive users)
    // Inactive users still count against license quota since their emails are verified
    return outreachUsers.filter((user) => user.isWhitelisted).length;
  }, [outreachUsers]);

  const availableLicenses = organization?.availableLicenses ?? null;

  // Orgs with an active Stripe subscription buy extra seats in-app instead of
  // hitting a "no licenses" wall.
  const canSelfServeAddLicenses =
    organization?.billingType === "stripe" &&
    !!organization?.billing?.subscriptionId;

  // Check if selected user can be added (has no boring license and licenses available)
  const canAddSelectedUser = useMemo(() => {
    if (selectedUsers.size !== 1) return false;
    const selectedUserId = Array.from(selectedUsers)[0];
    const selectedUser = outreachUsers.find(user => user.id.toString() === selectedUserId);
    if (!selectedUser || selectedUser.isWhitelisted) return false;
    
    // Check if licenses are available
    if (availableLicenses !== null) {
      const seatsAvailable = availableLicenses - activeBoringUsersCount;
      return seatsAvailable > 0;
    }
    
    // If licenses not configured, allow (unlimited)
    return true;
  }, [selectedUsers, outreachUsers, availableLicenses, activeBoringUsersCount]);
  const seatsAvailable = availableLicenses !== null 
    ? Math.max(0, availableLicenses - activeBoringUsersCount)
    : 0; // Default to 0 if licenses not configured

  // Determine which license buttons to show based on selected users
  const licenseButtonState = useMemo(() => {
    if (selectedUsers.size === 0) {
      return { showAdd: false, showRemove: false };
    }

    const selectedUserObjects = Array.from(selectedUsers)
      .map(userId => outreachUsers.find(user => user.id.toString() === userId))
      .filter((user): user is OutreachDisplayUser => user !== undefined);

    if (selectedUserObjects.length === 0) {
      return { showAdd: false, showRemove: false };
    }

    const allHaveLicenses = selectedUserObjects.every(user => user.isWhitelisted);
    const allLackLicenses = selectedUserObjects.every(user => !user.isWhitelisted);
    const someHaveLicenses = selectedUserObjects.some(user => user.isWhitelisted);
    const someLackLicenses = selectedUserObjects.some(user => !user.isWhitelisted);

    // If all selected users have licenses, only show remove button
    if (allHaveLicenses) {
      return { showAdd: false, showRemove: true };
    }

    // If all selected users lack licenses, only show add button
    if (allLackLicenses) {
      return { showAdd: true, showRemove: false };
    }

    // If some have licenses and some don't, show both buttons
    if (someHaveLicenses && someLackLicenses) {
      return { showAdd: true, showRemove: true };
    }

    return { showAdd: false, showRemove: false };
  }, [selectedUsers, outreachUsers]);

  // Frozen display order, keyed by the set of visible people.
  const rowOrderRef = useRef(createRowOrderState());

  // Rows with a licence write in flight - used to show progress and to swallow
  // repeat clicks on the same person.
  const [savingLicenseIds, setSavingLicenseIds] = useState<Set<string>>(new Set());
  // Running allowed-emails set, so back-to-back toggles compose instead of
  // overwriting each other. Null means "rebuild from the organization".
  const pendingAllowedEmailsRef = useRef<string[] | null>(null);
  const licenseWriteQueueRef = useRef<Promise<unknown>>(Promise.resolve());

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...outreachUsers];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.fullName.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query)
      );
    }

    // Apply filters
    if (filters.status !== undefined && filters.status !== null) {
      const filter = filters.status;
      filtered = filtered.filter((user) => {
        if (filter.operator === "is") {
          return filter.value === "active" ? user.isActive : !user.isActive;
        } else if (filter.operator === "is not") {
          return filter.value === "active" ? !user.isActive : user.isActive;
        }
        return true;
      });
    }

    if (filters.boringLicense !== undefined && filters.boringLicense !== null) {
      const filter = filters.boringLicense;
      filtered = filtered.filter((user) => {
        if (filter.operator === "is") {
          return filter.value === "active" ? user.isWhitelisted : !user.isWhitelisted;
        } else if (filter.operator === "is not") {
          return filter.value === "active" ? !user.isWhitelisted : user.isWhitelisted;
        }
        return true;
      });
    }

    if (filters.role !== undefined && filters.role !== null) {
      const filter = filters.role;
      filtered = filtered.filter((user) => {
        if (filter.operator === "is") {
          // User matches if their role is ANY of the selected values
          return filter.values.includes(user.role || "");
        } else if (filter.operator === "is not") {
          // User matches if their role is NONE of the selected values
          return !filter.values.includes(user.role || "");
        }
        return true;
      });
    }

    if (filters.teams !== undefined && filters.teams !== null) {
      const filter = filters.teams;
      filtered = filtered.filter((user) => {
        if (filter.operator === "is") {
          // User matches if they have ANY of the selected teams
          return user.teams?.some(team => filter.values.includes(team)) || false;
        } else if (filter.operator === "is not") {
          // User matches if they have NONE of the selected teams
          return !user.teams?.some(team => filter.values.includes(team));
        }
        return true;
      });
    }

    // Licensed-first ordering, frozen so a licence toggle never reorders rows.
    // See orderUsersStably for the reasoning.
    return orderUsersStably(filtered, rowOrderRef.current);
  }, [outreachUsers, searchQuery, filters]);

  const fetchOutreachUsers = async () => {
    setIsLoading(true);
    
    const isHubSpotIntegration = organization?.initialIntegrationType === "hubspot";
    // Check organization auth status (source of truth)
    const orgIsHubSpotAuthed = organization?.isHubSpotAuthorized === true || !!organization?.hubSpotAuthentication;

    try {
      // Fetch from HubSpot if that's the active integration
      if (isHubSpotIntegration) {
        // Only fetch if actually authorized
        if (!orgIsHubSpotAuthed) {
          devLog("Skipping HubSpot users fetch - organization not authorized");
          setOutreachUsers([]);
          setIsLoading(false);
          return;
        }
        
        const { result, status } = await getHubSpotUsers();
        
        if (status === 401) {
          // Only warn if we thought we were authorized - token may have expired
          if (orgIsHubSpotAuthed) {
            devWarn("⚠️ HubSpot API returned 401 - token may have expired. Please re-authenticate.");
          }
          setOutreachUsers([]);
          setIsLoading(false);
          return;
        }
        
        if (status !== 200 || !result?.users) {
          console.error("Failed to fetch HubSpot users:", { status, result });
          setOutreachUsers([]);
          setIsLoading(false);
          return;
        }
        
        if (status === 200 && result?.users) {
          const whitelistedEmailsSet = new Set(
            (organization?.allowedOwnerEmails || []).map((email: string) =>
              email.toLowerCase().trim()
            )
          );
          
          const transformedUsers = result.users.map((user, index) => {
            const email = user.email || "";
            const firstName = user.firstName || "";
            const lastName = user.lastName || "";
            const fullName = `${firstName} ${lastName}`.trim() || email;
            const isWhitelisted = whitelistedEmailsSet.has(email.toLowerCase().trim());
            
            // Extract team names from HubSpot teams array
            const teamNames = user.teams?.map((team: { id: string; name: string; primary: boolean }) => team.name).filter(Boolean) || [];
            
            // Use role and avatar from Settings Users API (merged by backend)
            const role = user.superAdmin ? "Super Admin" : (user.roleName || "");
            
            return {
              id: parseInt(user.id, 10) || index + 1, // Convert string to number for OutreachDisplayUser compatibility
              email,
              firstName,
              lastName,
              fullName,
              avatarUrl: user.avatarUrl, // From Settings Users API
              isActive: !user.archived, // Use archived status from HubSpot
              isWhitelisted,
              role, // From Settings Users API
              teams: teamNames,
            };
          });
          
          setOutreachUsers(transformedUsers);
        }
        
        setIsLoading(false);
        return;
      }
      
      // Otherwise fetch from Outreach
      const { result, status } = await getOutreachUsers();

      if (status === 401) {
        devWarn("⚠️ Organization not authorized for Outreach. Please re-authenticate.");
        setOutreachUsers([]);
        return;
      }

      if (status === 200 && result?.users) {
        setOutreachDataComplete(result.relationshipsIncluded !== false);
        // Parse included resources (roles, teams, profiles)
        const includedResources = result.included || [];
        devLog("🔍 Included resources received:", {
          count: includedResources.length,
          types: includedResources.map((r: any) => r.type),
          resources: includedResources,
          fullResult: result, // Log entire result to see structure
        });
        
        // Log first few users to check relationships
        if (result.users.length > 0) {
          devLog("🔍 Sample user relationships:", {
            firstUser: {
              id: result.users[0].id,
              email: result.users[0].attributes?.email,
              relationships: result.users[0].relationships,
              hasRole: !!result.users[0].relationships?.role,
              hasTeams: !!result.users[0].relationships?.teams,
              fullUserObject: result.users[0], // Log full user to see structure
            }
          });
        }
        
        // Check if result structure is different
        devLog("🔍 Full API response structure:", {
          hasIncluded: 'included' in result,
          hasUsers: 'users' in result,
          resultKeys: Object.keys(result),
          includedType: typeof result.included,
          includedIsArray: Array.isArray(result.included),
        });
        
        const rolesMap = new Map();
        const teamsMap = new Map();
        const profilesMap = new Map();

        includedResources.forEach((resource: any) => {
          if (resource.type === "role") {
            rolesMap.set(resource.id, resource);
            devLog("✅ Added role to map:", {
              id: resource.id,
              name: resource.attributes?.name,
              title: resource.attributes?.title,
              label: resource.attributes?.label,
              allAttributes: resource.attributes,
              fullResource: resource
            });
          } else if (resource.type === "team") {
            teamsMap.set(resource.id, resource);
            devLog("✅ Added team to map:", {
              id: resource.id,
              name: resource.attributes?.name,
              title: resource.attributes?.title,
              label: resource.attributes?.label,
              allAttributes: resource.attributes,
              fullResource: resource
            });
          } else if (resource.type === "profile") {
            profilesMap.set(resource.id, resource);
            devLog("✅ Added profile to map:", {
              id: resource.id,
              name: resource.attributes?.name,
              title: resource.attributes?.title,
              label: resource.attributes?.label,
              allAttributes: resource.attributes,
              fullResource: resource
            });
          }
        });
        
        devLog("📊 Maps created:", {
          rolesCount: rolesMap.size,
          teamsCount: teamsMap.size,
          profilesCount: profilesMap.size,
          roleIds: Array.from(rolesMap.keys()),
          teamIds: Array.from(teamsMap.keys()),
          profileIds: Array.from(profilesMap.keys()),
        });
        
        // Warn if no included resources but users exist - likely missing scopes
        if (includedResources.length === 0 && result.users.length > 0) {
          devWarn("⚠️ No included resources received. Users may need to re-authenticate with profiles.read and teams.read scopes.");
          // Check if any users have relationships but we don't have the included data
          const usersWithRelationships = result.users.filter((u: OutreachUser) => 
            u.relationships?.profile || u.relationships?.teams
          );
          if (usersWithRelationships.length > 0) {
            toast.warn(
              "Profiles and teams are not showing because your Outreach connection needs to be re-authenticated. Please disconnect and reconnect Outreach in Settings to update permissions.",
              { autoClose: 10000 }
            );
          }
        }

        // Check if locked or active field is present in the API response
        // Check multiple users to be more accurate
        const usersWithStatusField = result.users.filter(
          (user: OutreachUser) => 
            user.attributes?.locked !== undefined || 
            user.attributes?.active !== undefined
        );
        const hasStatusField = usersWithStatusField.length > 0;
        setHasLockedField(hasStatusField);
        
        // Debug logging - check what fields are actually available
        if (result.users.length > 0) {
          const sampleUsers = result.users.slice(0, 3).map((user: OutreachUser) => {
            const attrs = user.attributes || {};
            return {
              id: user.id,
              email: attrs.email,
              locked: attrs.locked,
              active: attrs.active,
              hasLockedField: attrs.locked !== undefined,
              hasActiveField: attrs.active !== undefined,
              allAttributeKeys: Object.keys(attrs),
            };
          });
          devLog("Sample users from API:", sampleUsers);
          devLog(`Users with status field (locked or active): ${usersWithStatusField.length} out of ${result.users.length}`);
          
          // Log first user's full attributes to see what's available
          if (result.users[0]) {
            devLog("First user full attributes:", result.users[0].attributes);
          }
        }

        const transformedUsers: DataGridRow<OutreachDisplayUser>[] =
          result.users.map((user: OutreachUser) => {
            const attributes = user.attributes || {};
            const email = attributes.email || "";
            const firstName = attributes.firstName || "";
            const lastName = attributes.lastName || "";
            const name = attributes.name || "";
            const fullName =
              firstName && lastName
                ? `${firstName} ${lastName}`
                : name || email || "Unknown User";

            const emailLower = email.toLowerCase().trim();
            const isWhitelisted = whitelistedEmails.has(emailLower);

            // Extract profile from relationships (displayed as "Profile" column)
            let role: string | undefined;
            const profileRelationship = user.relationships?.profile?.data;
            if (profileRelationship) {
              devLog(`👤 User ${user.id} has profile relationship:`, profileRelationship);
              if (profilesMap.has(profileRelationship.id)) {
                const profileResource = profilesMap.get(profileRelationship.id);
                role =
                  profileResource.attributes?.name ||
                  profileResource.attributes?.title ||
                  profileResource.attributes?.label ||
                  undefined;
                devLog(`✅ Found profile for user ${user.id}:`, role);
              } else {
                devWarn(`⚠️ Profile ${profileRelationship.id} not found in map for user ${user.id}. Map has ${profilesMap.size} profiles.`);
                // If we have a relationship but no included data, it means scopes are missing
                if (profilesMap.size === 0) {
                  devWarn(`⚠️ Profile relationship exists but no profiles in included resources - likely missing profiles.read scope`);
                }
              }
            } else {
              devLog(`ℹ️ User ${user.id} has no profile relationship`);
            }

            // Extract teams from relationships
            let teams: string[] | undefined;
            const teamsRelationship = user.relationships?.teams?.data;
            if (teamsRelationship) {
              const teamRefsArray = Array.isArray(teamsRelationship) ? teamsRelationship : [teamsRelationship];
              devLog(`👤 User ${user.id} has teams relationship:`, {
                relationship: teamsRelationship,
                isArray: Array.isArray(teamsRelationship),
                teamIds: teamRefsArray.map((t: any) => t?.id),
                teamsMapSize: teamsMap.size,
                teamsMapKeys: Array.from(teamsMap.keys())
              });
              const teamRefs = teamRefsArray;

              const teamNames = teamRefs
                .map((teamRef: any) => {
                  if (teamRef?.id) {
                    devLog(`🔍 Looking up team ${teamRef.id} for user ${user.id}...`);
                    if (teamsMap.has(teamRef.id)) {
                      const teamResource = teamsMap.get(teamRef.id);
                      devLog(`🔍 Team resource found:`, {
                        id: teamRef.id,
                        resource: teamResource,
                        attributes: teamResource?.attributes,
                        allKeys: teamResource ? Object.keys(teamResource) : []
                      });
                      const teamName = teamResource?.attributes?.name || 
                                      teamResource?.attributes?.title ||
                                      teamResource?.attributes?.label ||
                                      teamResource?.attributes?.displayName ||
                                      null;
                      devLog(`✅ Found team ${teamRef.id} for user ${user.id}:`, teamName);
                      return teamName;
                    } else {
                      devWarn(`⚠️ Team ${teamRef.id} not found in map for user ${user.id}.`, {
                        teamId: teamRef.id,
                        mapSize: teamsMap.size,
                        mapKeys: Array.from(teamsMap.keys()),
                        mapHasId: teamsMap.has(teamRef.id),
                        userId: user.id,
                        userEmail: user.attributes?.email
                      });
                      // If we have a relationship but no included data, it means scopes are missing
                      if (teamsMap.size === 0) {
                        devWarn(`⚠️ Team relationship exists but no teams in included resources - likely missing teams.all scope`);
                      }
                    }
                  } else {
                    devWarn(`⚠️ Team reference missing ID:`, teamRef);
                  }
                  return null;
                })
                .filter(Boolean);

              if (teamNames.length > 0) {
                teams = teamNames;
                devLog(`✅ Final teams for user ${user.id}:`, teams);
              } else {
                devLog(`ℹ️ No team names found for user ${user.id} after processing ${teamRefs.length} team references`);
              }
            } else {
              devLog(`ℹ️ User ${user.id} has no teams relationship. Full relationships:`, user.relationships);
            }

            // Determine if user is active based on locked or active field
            // locked: true means inactive, locked: false means active
            // active: false means inactive, active: true means active
            // Prefer locked field if available, otherwise use active field
            let isActive: boolean;
            if (attributes.locked !== undefined) {
              isActive = !attributes.locked;
            } else if (attributes.active !== undefined) {
              isActive = attributes.active;
            } else {
              // If neither field is available, default to active (unlocked)
              // This happens when OAuth scopes don't include user status permissions
              isActive = true;
            }

            return {
              id: user.id,
              email,
              firstName,
              lastName,
              fullName,
              avatarUrl: attributes.avatarUrl,
              isActive,
              isWhitelisted,
              role,
              teams,
            };
          });

        setOutreachUsers(transformedUsers);
      }
    } catch (error) {
      console.error("Error fetching Outreach users", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOutreachUsers();
  }, [whitelistedEmails]);

  const handleRefresh = () => {
    fetchOutreachUsers();
  };

  const handleAddSelectedUser = async () => {
    // Check if user has selected anyone
    if (selectedUsers.size === 0) {
      toast.error("Please select at least one user to assign a license");
      return;
    }

    if (selectedUsers.size !== 1) {
      toast.error("Please select exactly one user to assign a license");
      return;
    }
    
    const selectedUserId = Array.from(selectedUsers)[0];
    const selectedUser = outreachUsers.find(user => user.id.toString() === selectedUserId);
    
    if (!selectedUser) {
      toast.error("Selected user not found");
      return;
    }

    if (selectedUser.isWhitelisted) {
      toast.error("Selected user already has a Boring license");
      return;
    }

    // Check license availability
    if (availableLicenses !== null) {
      const seatsAvailable = availableLicenses - activeBoringUsersCount;
      if (seatsAvailable <= 0) {
        // Self-serve orgs can buy more seats on the spot.
        if (canSelfServeAddLicenses) {
          setIsAddLicensesOpen(true);
        } else {
          toast.error(`No licenses available. You have ${activeBoringUsersCount} / ${availableLicenses} licenses in use.`);
        }
        return;
      }
    }

    try {
      // Get current allowedOwnerEmails
      const currentEmails = new Set(
        (organization?.allowedOwnerEmails || []).map((email) =>
          email.toLowerCase().trim()
        )
      );

      // Add the selected user's email
      const emailLower = selectedUser.email.toLowerCase().trim();
      if (!currentEmails.has(emailLower)) {
        currentEmails.add(emailLower);
      }

      // Chain writes so concurrent toggles reach the API in order instead of
      // racing each other.
      const emailsToWrite = Array.from(currentEmails);
      const send = licenseWriteQueueRef.current.then(() =>
        updateOrganizationAllowedEmails(emailsToWrite)
      );
      licenseWriteQueueRef.current = send.catch(() => undefined);
      const { status, result } = await send;

      if (status !== 200) {
        throw new Error(`Failed to add user (Status: ${status})`);
      }

      // Refresh organization data
      const orgResponse = await getMyOrganization();
      if (orgResponse.result) {
        // Convert from database format to app format
        const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
          orgResponse.result
        );
        setOrganization(convertedOrg);
        
        // Update users immediately with new whitelist status
        const newWhitelistedEmails = new Set(
          (orgResponse.result.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
        );
        
        setOutreachUsers((prevUsers) =>
          prevUsers.map((user) => ({
            ...user,
            isWhitelisted: newWhitelistedEmails.has(user.email.toLowerCase().trim()),
          }))
        );
        
        // Clear selection
        setSelectedUsers(new Set());
        
        // Refresh from API
        await fetchOutreachUsers();
        
        toast.success(`${selectedUser.fullName} has been added to Boring licenses`);
      }
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error("Failed to add user. Please try again.");
    }
  };

  const handleAddLicensesToSelected = async () => {
    if (selectedUsers.size === 0) {
      toast.error("Please select at least one user to add licenses");
      return;
    }

    const selectedUserObjects = Array.from(selectedUsers)
      .map(userId => outreachUsers.find(user => user.id.toString() === userId))
      .filter((user): user is OutreachDisplayUser => user !== undefined && !user.isWhitelisted);

    if (selectedUserObjects.length === 0) {
      toast.error("All selected users already have licenses");
      return;
    }

    // Check license availability
    if (availableLicenses !== null) {
      const seatsAvailable = availableLicenses - activeBoringUsersCount;
      if (seatsAvailable < selectedUserObjects.length) {
        // Self-serve orgs can buy more seats on the spot.
        if (canSelfServeAddLicenses) {
          setIsAddLicensesOpen(true);
        } else {
          toast.error(`Not enough licenses available. You need ${selectedUserObjects.length} licenses but only have ${seatsAvailable} available.`);
        }
        return;
      }
    }

    try {
      // Get current allowedOwnerEmails
      const currentEmails = new Set(
        (organization?.allowedOwnerEmails || []).map((email) =>
          email.toLowerCase().trim()
        )
      );

      // Add all selected users' emails
      selectedUserObjects.forEach(user => {
        const emailLower = user.email.toLowerCase().trim();
        if (!currentEmails.has(emailLower)) {
          currentEmails.add(emailLower);
        }
      });

      // Chain writes so concurrent toggles reach the API in order instead of
      // racing each other.
      const emailsToWrite = Array.from(currentEmails);
      const send = licenseWriteQueueRef.current.then(() =>
        updateOrganizationAllowedEmails(emailsToWrite)
      );
      licenseWriteQueueRef.current = send.catch(() => undefined);
      const { status, result } = await send;

      if (status !== 200) {
        throw new Error(`Failed to add licenses (Status: ${status})`);
      }

      // Refresh organization data
      const orgResponse = await getMyOrganization();
      if (orgResponse.result) {
        // Convert from database format to app format
        const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
          orgResponse.result
        );
        setOrganization(convertedOrg);
        
        // Update users immediately with new whitelist status
        const newWhitelistedEmails = new Set(
          (orgResponse.result.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
        );
        
        setOutreachUsers((prevUsers) =>
          prevUsers.map((user) => ({
            ...user,
            isWhitelisted: newWhitelistedEmails.has(user.email.toLowerCase().trim()),
          }))
        );
        
        // Clear selection
        setSelectedUsers(new Set());
        
        // Refresh from API
        await fetchOutreachUsers();
        
        const userNames = selectedUserObjects.map(u => u.fullName).join(", ");
        toast.success(
          selectedUserObjects.length === 1
            ? `${userNames} has been granted a Boring license`
            : `${selectedUserObjects.length} users have been granted Boring licenses`
        );
      }
    } catch (error) {
      console.error("Error adding licenses:", error);
      toast.error("Failed to add licenses. Please try again.");
    }
  };

  const handleRemoveLicensesFromSelected = async () => {
    if (selectedUsers.size === 0) {
      toast.error("Please select at least one user to remove licenses");
      return;
    }

    const selectedUserObjects = Array.from(selectedUsers)
      .map(userId => outreachUsers.find(user => user.id.toString() === userId))
      .filter((user): user is OutreachDisplayUser => user !== undefined && user.isWhitelisted);

    if (selectedUserObjects.length === 0) {
      toast.error("None of the selected users have licenses to remove");
      return;
    }

    try {
      // Get current allowedOwnerEmails
      const currentEmails = new Set(
        (organization?.allowedOwnerEmails || []).map((email) =>
          email.toLowerCase().trim()
        )
      );

      // Remove all selected users' emails
      selectedUserObjects.forEach(user => {
        const emailLower = user.email.toLowerCase().trim();
        currentEmails.delete(emailLower);
      });

      // Chain writes so concurrent toggles reach the API in order instead of
      // racing each other.
      const emailsToWrite = Array.from(currentEmails);
      const send = licenseWriteQueueRef.current.then(() =>
        updateOrganizationAllowedEmails(emailsToWrite)
      );
      licenseWriteQueueRef.current = send.catch(() => undefined);
      const { status, result } = await send;

      if (status !== 200) {
        throw new Error(`Failed to remove licenses (Status: ${status})`);
      }

      // Refresh organization data
      const orgResponse = await getMyOrganization();
      if (orgResponse.result) {
        // Convert from database format to app format
        const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
          orgResponse.result
        );
        setOrganization(convertedOrg);
        
        // Update users immediately with new whitelist status
        const newWhitelistedEmails = new Set(
          (orgResponse.result.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
        );
        
        setOutreachUsers((prevUsers) =>
          prevUsers.map((user) => ({
            ...user,
            isWhitelisted: newWhitelistedEmails.has(user.email.toLowerCase().trim()),
          }))
        );
        
        // Clear selection
        setSelectedUsers(new Set());
        
        // Refresh from API
        await fetchOutreachUsers();
        
        const userNames = selectedUserObjects.map(u => u.fullName).join(", ");
        toast.success(
          selectedUserObjects.length === 1
            ? `${userNames}'s Boring license has been removed`
            : `${selectedUserObjects.length} users' Boring licenses have been removed`
        );
      }
    } catch (error) {
      console.error("Error removing licenses:", error);
      toast.error("Failed to remove licenses. Please try again.");
    }
  };

  const handleExportSelectedUsers = () => {
    if (selectedUsers.size === 0) {
      toast.error("Please select at least one user to export");
      return;
    }

    const selectedUserObjects = Array.from(selectedUsers)
      .map(userId => outreachUsers.find(user => user.id.toString() === userId))
      .filter((user): user is OutreachDisplayUser => user !== undefined);

    if (selectedUserObjects.length === 0) {
      toast.error("No users found to export");
      return;
    }

    // Define CSV headers matching the table columns
    const headers = [
      "Name",
      "Email",
      "Teams",
      "Profile",
      "Boring License",
      "User Status"
    ];

    // Convert users to CSV rows
    const rows = selectedUserObjects.map(user => [
      user.fullName || "",
      user.email || "",
      (user.teams || []).join("; "),
      user.role || "",
      user.isWhitelisted ? "Yes" : "No",
      user.isActive ? "Active" : "Inactive"
    ]);

    // Escape CSV fields (handle commas, quotes, newlines)
    const escapeCSVField = (field: string): string => {
      if (field.includes(",") || field.includes('"') || field.includes("\n")) {
        return `"${field.replace(/"/g, '""')}"`;
      }
      return field;
    };

    // Build CSV content
    const csvContent = [
      headers.map(escapeCSVField).join(","),
      ...rows.map(row => row.map(escapeCSVField).join(","))
    ].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `users-export-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${selectedUserObjects.length} user${selectedUserObjects.length > 1 ? "s" : ""} to CSV`);
  };

  const isHubSpotLook = useIsHubSpotCrm();

  /* HubSpot's quick filters open the same filter editor as the chips, set to that filter. */
  const openHubSpotFilter = (type: UsersFilterType, anchor: HTMLElement) => {
    setSelectedFilterType(type);
    if (type === "status" || type === "boringLicense") {
      const current = filters[type];
      setFilterOperator((current?.operator as "is" | "is not") || "is");
      setFilterValue(current?.value || "");
    } else {
      const current = type === "role" ? filters.role : filters.teams;
      setFilterOperator((current?.operator as "is" | "is not") || "is");
      setFilterValues(current?.values || []);
    }
    setFilterButtonEl(anchor as HTMLButtonElement);
    setFilterConfigAnchorEl(anchor as HTMLButtonElement);
  };

  /* Buying more seats goes through our support chat, or the booking page when it isn't loaded. */
  const openBuyLicenses = () => {
    openSupportChat();
  };

  const handleToggleLicense = async (userId: string, email: string, checked: boolean) => {
    // Ignore repeat clicks on a row that is already mid-flight.
    if (savingLicenseIds.has(userId)) return;

    // If turning on, check license availability
    if (checked && !whitelistedEmails.has(email.toLowerCase().trim())) {
      if (availableLicenses !== null) {
        const seatsAvailable = availableLicenses - activeBoringUsersCount;
        if (seatsAvailable <= 0) {
          // Self-serve orgs can buy more seats on the spot.
          if (canSelfServeAddLicenses) {
            setIsAddLicensesOpen(true);
          } else {
            toast.error(`No licenses available. You have ${activeBoringUsersCount} / ${availableLicenses} licenses in use.`);
          }
          return;
        }
      }
    }

    const emailLower = email.toLowerCase().trim();
    const user = outreachUsers.find(u => u.id.toString() === userId);
    const userName = user?.fullName || email;

    // Optimistic update - update UI immediately
    setOutreachUsers((prevUsers) =>
      prevUsers.map((user) => {
        if (user.id.toString() === userId) {
          return {
            ...user,
            isWhitelisted: checked,
          };
        }
        return user;
      })
    );
    setSavingLicenseIds((prev) => new Set(prev).add(userId));

    try {
      // Build from the running set rather than from `organization`, which is a
      // snapshot captured when this handler was created. Toggling two people
      // in quick succession used to compute both writes from the same stale
      // base, so the second request silently reverted the first.
      const currentEmails = new Set(
        pendingAllowedEmailsRef.current ??
          (organization?.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
      );

      if (checked) {
        // Add email to allowed list
        if (!currentEmails.has(emailLower)) {
          currentEmails.add(emailLower);
        }
      } else {
        // Remove email from allowed list
        currentEmails.delete(emailLower);
      }

      pendingAllowedEmailsRef.current = Array.from(currentEmails);

      // Chain writes so concurrent toggles reach the API in order instead of
      // racing each other.
      const emailsToWrite = Array.from(currentEmails);
      const send = licenseWriteQueueRef.current.then(() =>
        updateOrganizationAllowedEmails(emailsToWrite)
      );
      licenseWriteQueueRef.current = send.catch(() => undefined);
      const { status, result } = await send;

      if (status !== 200) {
        throw new Error(`Failed to update license (Status: ${status})`);
      }

      // Refresh organization data to sync with backend
      const orgResponse = await getMyOrganization();
      if (orgResponse.result) {
        // Convert from database format to app format
        const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
          orgResponse.result
        );
        setOrganization(convertedOrg);
        
        // Update users with backend state to ensure consistency
        const newWhitelistedEmails = new Set(
          (orgResponse.result.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
        );
        
        // Re-syncing every row from the server would clobber the optimistic
        // state of any toggle still in flight, so only reconcile rows that
        // aren't mid-write.
        setOutreachUsers((prevUsers) =>
          prevUsers.map((user) =>
            savingLicenseIds.has(user.id.toString()) && user.id.toString() !== userId
              ? user
              : {
                  ...user,
                  isWhitelisted: newWhitelistedEmails.has(
                    user.email.toLowerCase().trim()
                  ),
                }
          )
        );
        
        // Force a re-render by updating state
        setOrganization(convertedOrg);
        
        toast.success(
          checked 
            ? `${userName} has been granted a Boring license`
            : `${userName}'s Boring license has been removed`
        );
      }
    } catch (error) {
      console.error("Error toggling license:", error);
      
      // Revert optimistic update on error
      setOutreachUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (user.id.toString() === userId) {
            return {
              ...user,
              isWhitelisted: !checked, // Revert to previous state
            };
          }
          return user;
        })
      );
      
      // Drop the cached set so the next toggle rebuilds from the server.
      pendingAllowedEmailsRef.current = null;
      toast.error("Failed to update license. Please try again.");
    } finally {
      setSavingLicenseIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        // Once nothing is in flight, drop the running set so the next toggle
        // starts from fresh server state.
        if (next.size === 0) pendingAllowedEmailsRef.current = null;
        return next;
      });
    }
  };

  const handleReauthenticate = async () => {
    try {
      toast.info(
        "To update permissions, please:\n1. Go to Outreach → Settings → Apps & Integrations\n2. Revoke access to this app\n3. Then click Re-authenticate again",
        {
          autoClose: 10000,
        }
      );
      
      const { result, status } = await getOutreachAuthUrl({ forceReconsent: true });
      if (status === 200 && result?.outreachAuthUrl) {
        window.location.href = result.outreachAuthUrl;
      } else {
        toast.error("Failed to get Outreach authorization URL");
      }
    } catch (error: any) {
      console.error("Error getting Outreach auth URL", error);
      toast.error("Failed to get Outreach authorization URL");
    }
  };

  // Extract unique teams and roles from users
  useEffect(() => {
    const teams = new Set<string>();
    const roles = new Set<string>();
    
    outreachUsers.forEach((user) => {
      if (user.teams && user.teams.length > 0) {
        user.teams.forEach((team) => teams.add(team));
      }
      if (user.role) {
        roles.add(user.role);
      }
    });
    
    const teamsArray = Array.from(teams).sort();
    const rolesArray = Array.from(roles).sort();
    
    setAvailableTeams(teamsArray);
    setAvailableRoles(rolesArray);
    
    // Only set default team/role if we don't have one selected and options are available
    if (teamsArray.length > 0 && !selectedTeam && syncType === "team") {
      setSelectedTeam(teamsArray[0]);
    }
    if (rolesArray.length > 0 && !selectedRole && syncType === "role") {
      setSelectedRole(rolesArray[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outreachUsers]);

  const handleSyncLicenses = async () => {
    if (!signedInUser) {
      toast.error("You must be signed in to sync licenses");
      return;
    }

    // Never make role/team-based sync decisions from incomplete Outreach
    // data — it reads as "nobody matches" and revokes everyone's license.
    const isOutreachOrg =
      !organization?.initialIntegrationType ||
      organization.initialIntegrationType === "outreach";
    if (isOutreachOrg && !outreachDataComplete) {
      toast.error(
        "Outreach didn't return role/team data on the last refresh. Sync was blocked to protect existing licenses — refresh users and try again."
      );
      return;
    }

    // Determine which teams and roles to sync
    let teamsToSync: string[] = [];
    let rolesToSync: string[] = [];
    const hasSavedConfig = organization?.licenseSyncTeams && organization.licenseSyncTeams.length > 0 || organization?.licenseSyncRoles && organization.licenseSyncRoles.length > 0;

    if (useSavedConfig && hasSavedConfig) {
      // Use saved configuration from organization settings
      teamsToSync = organization.licenseSyncTeams || [];
      rolesToSync = organization.licenseSyncRoles || [];
    } else {
      // Use current dialog selections (from chip-based UI or old UI)
      // Check if using chip-based selections first
      if (selectedTeams.length > 0) {
        teamsToSync = selectedTeams;
      } else if (selectedTeam) {
        teamsToSync = [selectedTeam];
      }
      
      if (selectedRoles.length > 0) {
        rolesToSync = selectedRoles;
      } else if (selectedRole) {
        rolesToSync = [selectedRole];
      }

      if (teamsToSync.length === 0 && rolesToSync.length === 0) {
        toast.error("Please select at least one team or role to sync");
        return;
      }
    }

    setIsSyncing(true);
    try {
      // Get active users from selected teams/roles who don't already have a license
      const activeUsers = outreachUsers.filter((user) => {
        const isActive = user.isActive;
        const isNotWhitelisted = !user.isWhitelisted;
        
        const userTeams = user.teams || [];
        const userRole = user.role || "";
        const hasTeams = userTeams.length > 0;
        const hasRole = userRole !== "";
        
        // Check if user matches any of the selected teams
        let matchesTeam = false;
        if (teamsToSync.length > 0) {
          matchesTeam = userTeams.some(team => teamsToSync.includes(team)) || false;
        }
        
        // Check if user matches any of the selected roles
        let matchesRole = false;
        if (rolesToSync.length > 0) {
          matchesRole = rolesToSync.includes(userRole);
        }
        
        // Determine if user should be synced based on explicit selections
        // User matches if they match ANY configured team OR ANY configured role (OR logic)
        let matchesFilter = false;
        
        if (teamsToSync.length > 0 && rolesToSync.length > 0) {
          // Both teams and roles are configured: user must match at least one
          matchesFilter = matchesTeam || matchesRole;
        } else if (teamsToSync.length > 0) {
          // Only teams are configured: user must match a team
          matchesFilter = matchesTeam;
        } else if (rolesToSync.length > 0) {
          // Only roles are configured: user must match a role
          matchesFilter = matchesRole;
        }
        
        return isActive && matchesFilter && isNotWhitelisted;
      });

      // Get users to remove licenses from:
      // 1. Inactive users (locked in Outreach)
      const inactiveUsers = outreachUsers.filter((user) => {
        const isInactive = !user.isActive;
        const isWhitelisted = user.isWhitelisted;
        return isInactive && isWhitelisted;
      });

      // 2. Active users who moved to teams/roles not in the sync list
      const usersInWrongTeams = outreachUsers.filter((user) => {
        const isActive = user.isActive;
        const isWhitelisted = user.isWhitelisted;
        if (!isActive || !isWhitelisted) return false;
        
        const userTeams = user.teams || [];
        const userRole = user.role || "";
        const hasTeams = userTeams.length > 0;
        const hasRole = userRole !== "";
        
        // Check if user matches any configured team or role (OR logic)
        // Remove license only if user doesn't match ANY team AND doesn't match ANY role
        const isInSyncedTeam = teamsToSync.length > 0 && userTeams.some(team => teamsToSync.includes(team));
        const hasSyncedRole = rolesToSync.length > 0 && rolesToSync.includes(userRole);
        
        // User keeps license if they match ANY configured team OR role
        // Remove license if they match NEITHER
        return !isInSyncedTeam && !hasSyncedRole;
        
        return false;
      });

      const usersToRemove = [...inactiveUsers, ...usersInWrongTeams];

      if (activeUsers.length === 0 && usersToRemove.length === 0) {
        toast.info("No users found to sync. All active users from selected teams/profiles already have licenses, and no licenses need to be revoked.");
        setIsSyncDialogOpen(false);
        setIsSyncing(false);
        return;
      }

      // Get current allowedOwnerEmails
      const currentEmails = new Set(
        (organization?.allowedOwnerEmails || []).map((email) =>
          email.toLowerCase().trim()
        )
      );

      // STEP 1: First, remove licenses from users to free up seats
      // This ensures we have enough licenses available before assigning to active users
      usersToRemove.forEach((user) => {
        const emailLower = user.email.toLowerCase().trim();
        if (currentEmails.has(emailLower)) {
          currentEmails.delete(emailLower);
        }
      });

      // STEP 2: Calculate available licenses AFTER removing users
      // Count only active users who will have licenses after removal
      const activeUsersWithLicensesAfterRemoval = outreachUsers.filter((user) => {
        const isActive = user.isActive;
        const emailLower = user.email.toLowerCase().trim();
        const willHaveLicense = currentEmails.has(emailLower);
        return isActive && willHaveLicense;
      }).length;

      // STEP 3: Check if we have enough licenses for all users we want to sync
      if (availableLicenses !== null) {
        // Calculate seats available after removing users
        const seatsAvailable = availableLicenses - activeUsersWithLicensesAfterRemoval;
        if (activeUsers.length > seatsAvailable) {
          toast.error(
            `Not enough licenses available. You want to sync ${activeUsers.length} users but only have ${seatsAvailable} license(s) available after removing ${usersToRemove.length} license(s) (${activeUsersWithLicensesAfterRemoval} active users will have licenses / ${availableLicenses} total).`
          );
          setIsSyncDialogOpen(false);
          setIsSyncing(false);
          return;
        }
      }

      // STEP 4: Now add licenses to active users (after we've confirmed we have enough)
      activeUsers.forEach((user) => {
        const emailLower = user.email.toLowerCase().trim();
        if (!currentEmails.has(emailLower)) {
          currentEmails.add(emailLower);
        }
      });

      // Save sync configuration if we're setting it up (not using saved config or no saved config exists)
      // This allows the configuration to persist for future one-click syncs
      // Save if: user manually configured (not using saved config) OR no saved config exists
      // Also save if user manually selected teams/roles (even if useSavedConfig is true, they might have unchecked it)
      const userManuallyConfigured = selectedTeams.length > 0 || selectedRoles.length > 0;
      const shouldSaveConfig = !useSavedConfig || !hasSavedConfig || userManuallyConfigured;
      
      if (shouldSaveConfig) {
        try {
          // Only save the sync config fields — do NOT spread the entire organization
          // object, as that would overwrite allowedOwnerEmails with stale React state
          const syncConfigOnly = {
            licenseSyncTeams: teamsToSync,
            licenseSyncRoles: rolesToSync,
          };

          const currentSettings = {
            userSettings: {
              firstName: signedInUser?.firstName || "",
              title: signedInUser?.title || "",
              profile: signedInUser?.profile || ProfileType.DEFAULT,
              phoneNumber: signedInUser?.phoneNumber || "",
              email: signedInUser?.email || "",
            },
            organizationSettings: syncConfigOnly,
          };

          const cleanedPayload = removeUndefinedAndNull(currentSettings);
          await updateOrganizationSettings(cleanedPayload);
          // Refresh organization to get updated config
          const orgResponse = await getMyOrganization();
          if (orgResponse.result) {
            // Convert from database format to app format
            const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
              orgResponse.result
            );
            setOrganization(convertedOrg);
          }
          devLog("✅ Saved sync configuration:", { teamsToSync, rolesToSync });
        } catch (error) {
          console.error("Failed to save sync configuration:", error);
          // Continue with sync even if saving config fails
        }
      }

      // Update organization settings
      devLog("Syncing licenses for users:", {
        teamsToSync,
        rolesToSync,
        activeUsersCount: activeUsers.length,
        activeUsers: activeUsers.map(u => ({ email: u.email, name: u.fullName })),
        usersToRemoveCount: usersToRemove.length,
        inactiveUsersCount: inactiveUsers.length,
        usersInWrongTeamsCount: usersInWrongTeams.length,
        usersToRemove: usersToRemove.map(u => ({ email: u.email, name: u.fullName })),
        currentEmailsCount: currentEmails.size,
        newEmailsToAdd: activeUsers.map(u => u.email.toLowerCase().trim()),
        emailsToRemove: usersToRemove.map(u => u.email.toLowerCase().trim()),
      });

      // Update allowedOwnerEmails directly in Firestore (like Retool does)
      // This bypasses the Firebase Function that was causing 500 errors
      if (!organization?.id) {
        toast.error("Organization ID not found");
        return;
      }

      devLog("Updating allowedOwnerEmails via Firebase Function:", {
        organizationId: organization.id,
        allowedOwnerEmails: Array.from(currentEmails),
      });

      const { status, result } = await updateOrganizationAllowedEmails(
        Array.from(currentEmails)
      );

      devLog("Update allowedOwnerEmails response:", { status, result });

      if (status !== 200) {
        throw new Error(`Failed to update allowed emails (Status: ${status})`);
      }

      devLog("Successfully updated allowedOwnerEmails");

      // Refresh organization data first to update whitelistedEmails
      const orgResponse = await getMyOrganization();
      if (orgResponse.result) {
        // Convert from database format to app format before setting organization state
        const convertedOrg = convertDbOrganizationSettingsToAppOrganizationSettings(
          orgResponse.result
        );
        // Update organization state - this will trigger whitelistedEmails to recalculate
        setOrganization(convertedOrg);
        
        // Update users immediately with new whitelist status
        // Use the new allowedOwnerEmails from the response
        const newWhitelistedEmails = new Set(
          (orgResponse.result.allowedOwnerEmails || []).map((email) =>
            email.toLowerCase().trim()
          )
        );
        
        // Update existing users' isWhitelisted status immediately
        setOutreachUsers((prevUsers) =>
          prevUsers.map((user) => ({
            ...user,
            isWhitelisted: newWhitelistedEmails.has(user.email.toLowerCase().trim()),
          }))
        );
        
        // Also refresh from API to ensure we have latest data
        await fetchOutreachUsers();
        
        // Build success message
        const teamsText = teamsToSync.length > 0 ? `${teamsToSync.length} team(s)` : "";
        const rolesText = rolesToSync.length > 0 ? `${rolesToSync.length} role(s)` : "";
        const syncScopeText = [teamsText, rolesText].filter(Boolean).join(" and ");
        
        let successMessage = "";
        if (activeUsers.length > 0 && usersToRemove.length > 0) {
          successMessage = `Successfully synced licenses: added ${activeUsers.length} license(s) to active users from ${syncScopeText} and revoked ${usersToRemove.length} license(s) (${inactiveUsers.length} inactive, ${usersInWrongTeams.length} moved to non-synced teams/profiles)`;
        } else if (activeUsers.length > 0) {
          successMessage = `Successfully synced ${activeUsers.length} license(s) for active users from ${syncScopeText}`;
        } else if (usersToRemove.length > 0) {
          successMessage = `Successfully revoked ${usersToRemove.length} license(s) (${inactiveUsers.length} inactive, ${usersInWrongTeams.length} moved to non-synced teams/profiles)`;
        }
        
        toast.success(successMessage);
        setIsSyncDialogOpen(false);
      } else {
        toast.error("Failed to refresh organization data");
      }
    } catch (error: any) {
      console.error("Error syncing licenses", error);
      const errorMessage = error?.message || error?.toString() || "Unknown error";
      console.error("Error details:", {
        message: errorMessage,
        response: error?.response,
        data: error?.data,
      });
      toast.error(`Failed to sync licenses: ${errorMessage}`);
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      {!isHubSpotLook && <style>{`
        * {
          box-sizing: border-box;
        }
        
        body {
          overflow-x: hidden;
        }
        
        .p-3 {
          padding: 0 !important;
        }
        
        .search-wrapper {
          width: 180px !important;
          min-width: 180px !important;
          max-width: 180px !important;
          flex-shrink: 0 !important;
        }
        @media (max-width: 1024px) {
          .search-wrapper {
            width: 180px !important;
            min-width: 180px !important;
            max-width: 180px !important;
            flex-shrink: 0 !important;
          }
        }
        
        @media (max-width: 768px) {
          .status-bar {
            flex-direction: column;
            align-items: flex-start !important;
            gap: ${SPACINGS.xs} !important;
            padding: ${SPACINGS.md} ${SPACINGS.md} !important;
          }
          
          .toolbar {
            flex-direction: column;
            align-items: stretch !important;
            padding: ${SPACINGS.md} ${SPACINGS.md} !important;
          }
          
          .search-wrapper {
            width: 180px !important;
            min-width: 180px !important;
            max-width: 180px !important;
            flex-shrink: 0 !important;
          }
          
          .users-header {
            padding: ${SPACINGS.md} ${SPACINGS.md} ${SPACINGS.md} ${SPACINGS.md} !important;
          }
          
          .data-grid-wrapper {
            padding: 0 ${SPACINGS.md} ${SPACINGS.md} ${SPACINGS.md} !important;
            overflow-x: auto !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          
          .data-grid-wrapper > * {
            min-width: 0 !important;
          }
        }
        
        @media (max-width: 1024px) {
          .data-grid-wrapper {
            overflow-x: auto !important;
          }
        }
        
        @media (max-width: 480px) {
          .search-wrapper {
            width: 180px !important;
            min-width: 180px !important;
            max-width: 180px !important;
          }
          
          .users-header {
            padding: ${SPACINGS.sm} ${SPACINGS.sm} !important;
          }
          
          .toolbar {
            padding: ${SPACINGS.sm} ${SPACINGS.sm} !important;
            gap: ${SPACINGS.sm} !important;
          }
          
          .status-bar {
            padding: ${SPACINGS.sm} ${SPACINGS.sm} !important;
            font-size: 12px !important;
          }
          
          .data-grid-wrapper {
            padding: 0 ${SPACINGS.sm} ${SPACINGS.sm} ${SPACINGS.sm} !important;
          }
        }
      `}</style>}
      {isHubSpotLook ? (
        <HubSpotUsersPage
          users={filteredAndSortedUsers as OutreachDisplayUser[]}
          allUsers={outreachUsers as OutreachDisplayUser[]}
          isLoading={isLoading}
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          filterCounts={{
            status: filters.status ? 1 : 0,
            boringLicense: filters.boringLicense ? 1 : 0,
            role: filters.role ? Math.max(1, filters.role.values.length) : 0,
            team: filters.teams ? Math.max(1, filters.teams.values.length) : 0,
          }}
          filterTypes={[
            "status",
            "boringLicense",
            ...(availableRoles.length > 0 ? (["role"] as const) : []),
            ...(availableTeams.length > 0 ? (["team"] as const) : []),
          ]}
          onOpenFilter={openHubSpotFilter}
          onClearFilter={(type) => setFilters({ ...filters, [type === "team" ? "teams" : type]: null })}
          onClearAll={() => setFilters({})}
          onOpenAdvancedFilters={(anchor) => {
            setFilterButtonEl(anchor as HTMLButtonElement);
            setFilterAnchorEl(anchor as HTMLButtonElement);
          }}
          selected={selectedUsers}
          onSelect={setSelectedUsers}
          onToggleLicense={handleToggleLicense}
          savingIds={savingLicenseIds}
          availableLicenses={availableLicenses}
          activeCount={activeBoringUsersCount}
          seatsAvailable={seatsAvailable}
          showAddLicense={licenseButtonState.showAdd}
          showRemoveLicense={licenseButtonState.showRemove}
          onAddLicenses={handleAddLicensesToSelected}
          onRemoveLicenses={handleRemoveLicensesFromSelected}
          onRefresh={handleRefresh}
          onSync={() => {
            setExpandedAddingUsers(false);
            setExpandedRemovingUsers(false);
            setIsSyncDialogOpen(true);
          }}
          onBuy={openBuyLicenses}
          onExportSelected={handleExportSelectedUsers}
        />
      ) : (
      <Card style={{ padding: 0, overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <Grid container direction="column" gap={0} style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
          {/* Header Section */}
          <div 
            className="users-header"
            style={{ 
            padding: `${SPACINGS.xl} ${SPACINGS.xl} ${SPACINGS.xl} ${SPACINGS.xl}`,
            position: "sticky",
            top: 0,
            // Match the Card surface so the sticky header doesn't render as a
            // darker band in dark mode.
            backgroundColor: `var(--color-backgroundWhite, var(--color-backgroundPrimary, ${COLORS.backgroundPrimary}))`,
            zIndex: 10,
            paddingBottom: SPACINGS.md,
          }}>
            <Text
              variant={TextVariant.xl}
              style={{
                fontFamily: '"Work Sans", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: "1.5rem",
                fontWeight: 550,
                lineHeight: "1.5",
                color: "var(--color-foregroundPrimary, #454262)",
                marginBottom: 0,
              }}
            >
              Users
            </Text>
            <Text
              variant={TextVariant.sm}
              style={{
                fontSize: "14px",
                color: "var(--color-foregroundSecondary, #686783)",
                lineHeight: "1.5",
                marginBottom: SPACINGS.md,
              }}
            >
              Manage your users manually or automatically sync boring licenses to specific teams using the{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setExpandedAddingUsers(false);
                  setExpandedRemovingUsers(false);
                  setIsSyncDialogOpen(true);
                }}
                style={{
                  color: "#5951ff",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                Sync licenses
              </a>
              {" "}button.
            </Text>
          </div>
          
          {/* Warning Banner if locked field is missing */}
          {hasLockedField === false && (
            <div style={{
              margin: `0 ${SPACINGS.xl} ${SPACINGS.lg} ${SPACINGS.xl}`,
              backgroundColor: COLORS.chartEmailQuestionable + "20",
              border: `1px solid var(--color-chartEmailQuestionable, ${COLORS.chartEmailQuestionable})`,
              borderRadius: RADII.md,
              padding: SPACINGS.md,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div style={{ flex: 1 }}>
                <Text
                  variant={TextVariant.sm}
                  style={{
                    fontSize: "14px",
                    fontWeight: FONT_WEIGHTS.semibold,
                    color: "var(--color-foregroundPrimary, #454262)",
                    marginBottom: SPACINGS.xs,
                  }}
                >
                  User status data unavailable
                </Text>
                <Text
                  variant={TextVariant.sm}
                  style={{
                    fontSize: "13px",
                    color: "var(--color-foregroundSecondary, #686781)",
                  }}
                >
                  Unable to determine active/inactive status for users. The API may not be returning the locked field. All users are currently showing as active.
                </Text>
              </div>
              <Button
                variant={ButtonVariants.brand}
                onClick={handleReauthenticate}
                style={{ 
                  marginLeft: SPACINGS.md,
                  maxWidth: "150px",
                  minWidth: "150px",
                }}
              >
                Re-authenticate
              </Button>
            </div>
          )}

          {/* Status Bar */}
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            padding: `${SPACINGS.md} ${SPACINGS.xl} ${SPACINGS.md} ${SPACINGS.xl}`,
            borderBottom: `1px solid var(--color-borderDark, ${COLORS.borderDark})`,
            gap: SPACINGS.md,
            fontSize: "14px",
            color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})`,
            flexWrap: "wrap",
          }}
          className="status-bar"
          >
            <span>{filteredAndSortedUsers.length} results</span>
            <div style={{ display: "flex", alignItems: "center", gap: SPACINGS.md, flexWrap: "wrap" }}>
              {availableLicenses !== null ? (
                <span>{activeBoringUsersCount} / {availableLicenses} boring licenses assigned, {seatsAvailable} available</span>
              ) : (
                <span>{activeBoringUsersCount} boring licenses assigned, {seatsAvailable} available</span>
              )}
            </div>
          </div>

          {/* Toolbar - Search and Actions */}
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            padding: `${SPACINGS.lg} ${SPACINGS.xl}`,
            flexWrap: "wrap",
            gap: SPACINGS.md,
          }}
          className="toolbar"
          >
            {/* Left Side - Search and Filter */}
            <div style={{ display: "flex", alignItems: "center", gap: SPACINGS.lg, flex: 1, flexWrap: "wrap", minWidth: 0 }}>
              {/* Search Bar — Outreach-style full-width rounded bar */}
              <div style={{ position: "relative", flex: 1, minWidth: "240px", maxWidth: "520px" }}
              className="search-wrapper app-search"
              >
                <Input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <div style={{ paddingLeft: "14px", marginRight: "8px", display: "flex", alignItems: "center", color: `var(--color-foregroundSecondary, ${COLORS.foregroundSecondary})` }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L19.2928 20.7071C19.6833 21.0977 20.3165 21.0977 20.707 20.7071C21.0975 20.3166 21.0975 19.6835 20.707 19.2929L16.3199 14.9057C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2ZM4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    ),
                    endAdornment: (
                      <div style={{ paddingRight: "12px", display: "flex", alignItems: "center", color: `var(--color-foregroundSecondaryLight, ${COLORS.foregroundSecondaryLight})` }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                          <path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                      </div>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "38px",
                      borderRadius: "10px",
                      backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                      paddingLeft: "18px",
                      paddingRight: "16px",
                    },
                  }}
                  inputProps={{
                    style: { paddingLeft: "0px", paddingTop: "8px", paddingBottom: "8px" },
                  }}
                />
              </div>

              {/* Active Filter Chips */}
              {filters.status && (
                <Chip
                  label={
                    <span>
                      <span style={{ fontWeight: FONT_WEIGHTS.medium }}>Status</span>{" "}
                      {filters.status.value === ""
                        ? filters.status.operator
                        : `${filters.status.operator} ${filters.status.value === "active" ? "Active" : "Inactive"}`}
                    </span>
                  }
                  onClick={() => {
                    if (filters.status) {
                      const statusFilter = filters.status;
                      setSelectedFilterType("status");
                      setFilterOperator(statusFilter.operator as "is" | "is not");
                      setFilterValue(statusFilter.value);
                      setFilterConfigAnchorEl(filterButtonEl);
                    }
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setFilters({ ...filters, status: null });
                  }}
                  deleteIcon={
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                      data-testid="CancelIcon"
                      style={{ width: "14px", height: "14px" }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                      <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                    </svg>
                  }
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#5e5eaf",
                    border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    borderRadius: "20px",
                    height: "36px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    fontWeight: FONT_WEIGHTS.light,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f9f9fb",
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus-visible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:active": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&.Mui-focusVisible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "& .MuiChip-label": {
                      color: "#5e5eaf",
                      fontWeight: FONT_WEIGHTS.light,
                      cursor: "pointer",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                    },
                    "& .MuiChip-deleteIcon": {
                      width: "14px !important",
                      height: "14px !important",
                      marginRight: "0px",
                      marginLeft: "8px",
                      padding: "0 !important",
                      display: "flex !important",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": {
                        width: "14px !important",
                        height: "14px !important",
                      },
                      "& circle": {
                        fill: "#5e5eaf !important",
                        transition: "fill 0.2s ease",
                      },
                      "& path": {
                        fill: "#ffffff !important",
                      },
                      "&:hover": {
                        "& circle": {
                          fill: "#4a4a9f !important",
                        },
                      },
                    },
                  }}
                />
              )}
              {filters.boringLicense && (
                <Chip
                  label={
                    <span>
                      <span style={{ fontWeight: FONT_WEIGHTS.medium }}>Boring license</span>{" "}
                      {filters.boringLicense.value === ""
                        ? filters.boringLicense.operator
                        : `${filters.boringLicense.operator} ${filters.boringLicense.value === "active" ? "Active" : "Inactive"}`}
                    </span>
                  }
                  onClick={() => {
                    if (filters.boringLicense) {
                      const boringLicenseFilter = filters.boringLicense;
                      setSelectedFilterType("boringLicense");
                      setFilterOperator(boringLicenseFilter.operator as "is" | "is not");
                      setFilterValue(boringLicenseFilter.value);
                      setFilterConfigAnchorEl(filterButtonEl);
                    }
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setFilters({ ...filters, boringLicense: null });
                  }}
                  deleteIcon={
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                      data-testid="CancelIcon"
                      style={{ width: "14px", height: "14px" }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                      <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                    </svg>
                  }
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#5e5eaf",
                    border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    borderRadius: "20px",
                    height: "36px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    fontWeight: FONT_WEIGHTS.light,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f9f9fb",
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus-visible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:active": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&.Mui-focusVisible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "& .MuiChip-label": {
                      color: "#5e5eaf",
                      fontWeight: FONT_WEIGHTS.light,
                      cursor: "pointer",
                      paddingLeft: "0px",
                      paddingRight: "0px",
                    },
                    "& .MuiChip-deleteIcon": {
                      width: "14px !important",
                      height: "14px !important",
                      marginRight: "0px",
                      marginLeft: "8px",
                      padding: "0 !important",
                      display: "flex !important",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": {
                        width: "14px !important",
                        height: "14px !important",
                      },
                      "& circle": {
                        fill: "#5e5eaf !important",
                        transition: "fill 0.2s ease",
                      },
                      "& path": {
                        fill: "#ffffff !important",
                      },
                      "&:hover": {
                        "& circle": {
                          fill: "#4a4a9f !important",
                        },
                      },
                    },
                  }}
                />
              )}
              {filters.role && (
                <Chip
                  label={
                    <span>
                      <span style={{ fontWeight: FONT_WEIGHTS.medium }}>Profile</span>{" "}
                      {filters.role.values.length === 0
                        ? filters.role.operator
                        : filters.role.values.length === 1
                          ? `${filters.role.operator} ${filters.role.values[0]}`
                          : `${filters.role.operator} ${filters.role.values.length} profiles`}
                    </span>
                  }
                  onClick={() => {
                    if (filters.role) {
                      const roleFilter = filters.role;
                      setSelectedFilterType("role");
                      setFilterOperator(roleFilter.operator as "is" | "is not");
                      setFilterValues(roleFilter.values);
                      setFilterConfigAnchorEl(filterButtonEl);
                    }
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setFilters({ ...filters, role: null });
                  }}
                  deleteIcon={
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                      data-testid="CancelIcon"
                      style={{ width: "18px", height: "18px" }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                      <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                    </svg>
                  }
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#5e5eaf",
                    border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    borderRadius: "20px",
                    height: "36px",
                    fontWeight: FONT_WEIGHTS.light,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f9f9fb",
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus-visible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:active": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&.Mui-focusVisible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "& .MuiChip-label": {
                      color: "#5e5eaf",
                      fontWeight: FONT_WEIGHTS.light,
                      cursor: "pointer",
                    },
                    "& .MuiChip-deleteIcon": {
                      width: "18px !important",
                      height: "18px !important",
                      marginRight: "4px",
                      padding: "0 !important",
                      display: "flex !important",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": {
                        width: "18px !important",
                        height: "18px !important",
                      },
                      "& circle": {
                        fill: "#5e5eaf !important",
                      },
                      "& path": {
                        fill: "#ffffff !important",
                      },
                    },
                  }}
                />
              )}
              {filters.teams && (
                <Chip
                  label={
                    <span>
                      <span style={{ fontWeight: FONT_WEIGHTS.medium }}>Team</span>{" "}
                      {filters.teams.values.length === 0
                        ? filters.teams.operator
                        : filters.teams.values.length === 1
                          ? `${filters.teams.operator} ${filters.teams.values[0]}`
                          : `${filters.teams.operator} ${filters.teams.values.length} teams`}
                    </span>
                  }
                  onClick={() => {
                    if (filters.teams) {
                      const teamsFilter = filters.teams;
                      setSelectedFilterType("team");
                      setFilterOperator(teamsFilter.operator as "is" | "is not");
                      setFilterValues(teamsFilter.values);
                      setFilterConfigAnchorEl(filterButtonEl);
                    }
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    setFilters({ ...filters, teams: null });
                  }}
                  deleteIcon={
                    <svg
                      viewBox="0 0 24 24"
                      focusable="false"
                      aria-hidden="true"
                      data-testid="CancelIcon"
                      style={{ width: "18px", height: "18px" }}
                    >
                      <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                      <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                    </svg>
                  }
                  sx={{
                    backgroundColor: "#ffffff",
                    color: "#5e5eaf",
                    border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    borderRadius: "20px",
                    height: "36px",
                    fontWeight: FONT_WEIGHTS.light,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f9f9fb",
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:focus-visible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&:active": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "&.Mui-focusVisible": {
                      border: "1px solid rgba(89, 81, 255, 0.24) !important",
                    },
                    "& .MuiChip-label": {
                      color: "#5e5eaf",
                      fontWeight: FONT_WEIGHTS.light,
                      cursor: "pointer",
                    },
                    "& .MuiChip-deleteIcon": {
                      width: "18px !important",
                      height: "18px !important",
                      marginRight: "4px",
                      padding: "0 !important",
                      display: "flex !important",
                      alignItems: "center",
                      justifyContent: "center",
                      "& svg": {
                        width: "18px !important",
                        height: "18px !important",
                      },
                      "& circle": {
                        fill: "#5e5eaf !important",
                      },
                      "& path": {
                        fill: "#ffffff !important",
                      },
                    },
                  }}
                />
              )}

              {/* Add Filter Button */}
              <Button
                onClick={(e) => {
                  setFilterButtonEl(e.currentTarget);
                  setFilterAnchorEl(e.currentTarget);
                }}
                style={{
                  backgroundColor: "var(--color-backgroundBrandLight, #5e5eaf14)",
                  color: COLORS.foregroundBrand,
                  border: "none",
                  borderRadius: "9px",
                  height: "40px",
                  padding: "0 16px",
                  fontWeight: FONT_WEIGHTS.medium,
                }}
                className="assign-license-btn"
              >
                + Add filter
              </Button>
            </div>

            {/* Right Side - Actions */}
            <div style={{ display: "flex", alignItems: "center", gap: SPACINGS.xs }}>
              {/* Refresh Button (Outreach style - circular, grey) */}
              <IconButton
                size="small"
                onClick={handleRefresh}
                sx={{
                  color: `var(--color-foregroundBrand, ${COLORS.foregroundBrand})`,
                  backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                  borderRadius: "50%",
                  padding: "8px",
                  width: "36px",
                  height: "36px",
                  border: "none",
                  "&:hover": {
                    backgroundColor: `var(--color-borderDark, ${COLORS.borderDark})`,
                  },
                  "&:focus": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&:focus-visible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&.Mui-focusVisible": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "&:active": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.66793 5.87266C10.0173 5.13794 11.5732 4.85569 13.1007 5.06963C14.6281 5.28356 16.0417 5.98166 17.1293 7.05627C17.4421 7.36538 17.6961 7.6921 17.8985 7.9999H15.7574C15.2051 7.9999 14.7574 8.44762 14.7574 8.9999C14.7574 9.55219 15.2051 9.9999 15.7574 9.9999H20C20.5523 9.9999 21 9.55219 21 8.99991L21 4.99989C21 4.44761 20.5523 3.99989 20 3.99989C19.4477 3.99989 19 4.4476 19 4.99988L19 6.13706C18.8578 5.96845 18.703 5.79963 18.535 5.63361C17.1392 4.25443 15.3291 3.36222 13.3781 3.08896C11.4272 2.81571 9.43864 3.17579 7.71155 4.11614C5.98428 5.0566 4.61087 6.52731 3.80071 8.30881C2.99045 10.0905 2.7883 12.0849 3.22546 13.9908C3.66259 15.8965 4.71463 17.6081 6.22039 18.8687C7.72595 20.1292 9.60386 20.871 11.5708 20.9847C13.5378 21.0983 15.4898 20.5777 17.1332 19.4997C18.7767 18.4216 20.0231 16.8436 20.6821 15.0018C20.8681 14.4818 20.5974 13.9094 20.0774 13.7234C19.5574 13.5373 18.985 13.8081 18.799 14.3281C18.2877 15.7572 17.3189 16.986 16.0361 17.8275C14.7532 18.6691 13.2264 19.077 11.6862 18.988C10.146 18.899 8.67837 18.3182 7.50427 17.3352C6.33038 16.3524 5.51377 15.0213 5.17484 13.5436C4.83593 12.0661 4.99236 10.5197 5.62129 9.13675C6.2503 7.75359 7.31872 6.60727 8.66793 5.87266Z" fill={COLORS.foregroundBrand}/>
                </svg>
              </IconButton>

              {/* Sync Licenses Button */}
              <Button 
                onClick={() => setIsSyncDialogOpen(true)}
                className="sync-licenses-btn"
                style={{
                  marginLeft: SPACINGS.sm,
                  backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                  color: COLORS.foregroundBrand,
                  border: "none",
                  maxWidth: "140px",
                  minWidth: "140px",
                }}
              >
                Sync licenses
              </Button>

              {/* Add/Remove License Buttons (Outreach style - grey background, blue text) */}
              {selectedUsers.size > 0 && (
                <>
                  {licenseButtonState.showAdd && (
                    <Button
                      onClick={handleAddLicensesToSelected}
                      style={{
                        marginLeft: SPACINGS.sm,
                        backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                        color: COLORS.foregroundBrand,
                        border: "none",
                        maxWidth: "130px",
                        minWidth: "130px",
                      }}
                      className="add-license-btn"
                    >
                      Add license
                    </Button>
                  )}
                  {licenseButtonState.showRemove && (
                    <Button
                      onClick={handleRemoveLicensesFromSelected}
                      style={{
                        marginLeft: SPACINGS.sm,
                        backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                        color: COLORS.foregroundBrand,
                        border: "none",
                        maxWidth: "150px",
                        minWidth: "150px",
                      }}
                      className="remove-license-btn"
                    >
                      Remove license
                    </Button>
                  )}
                </>
              )}

              {/* Add Licenses Button */}
              <Button
                type="button"
                onClick={() => {
                                    openSupportChat();
                }}
                variant={ButtonVariants.brand}
                style={{
                  marginLeft: SPACINGS.sm,
                  maxWidth: "130px",
                  minWidth: "130px",
                }}
              >
                Buy licenses
              </Button>

              {/* Export Selected Users Button (far right) */}
              {selectedUsers.size > 0 && (
                <IconButton
                  size="small"
                  onClick={handleExportSelectedUsers}
                  sx={{
                    color: "var(--color-foregroundBrand, #5e5eaf)",
                    backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                    borderRadius: "50%",
                    padding: "8px",
                    width: "36px",
                    height: "36px",
                    marginLeft: SPACINGS.sm,
                    border: "none",
                    "&:hover": {
                      backgroundColor: "var(--color-borderDark, #e0e0e0)",
                    },
                    "&:focus": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                    "&:focus-visible": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                    "&.Mui-focusVisible": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                    "&:active": {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L12 15M12 15L7 10M12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 19H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </IconButton>
              )}
            </div>
          </div>

          {/* Data Grid */}
          <div 
            className="data-grid-wrapper"
            style={{ 
              padding: `0 ${SPACINGS.xl} ${SPACINGS.lg} ${SPACINGS.xl}`,
              overflowX: 'auto',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <DataGrid<OutreachDisplayUser>
              loading={isLoading}
              rows={filteredAndSortedUsers}
              rowHeight={64}
              columnHeaderHeight={48}
              columns={getOutreachUsersColumns(
              selectedUsers, 
              setSelectedUsers, 
              filteredAndSortedUsers,
              handleToggleLicense,
              availableLicenses,
              activeBoringUsersCount,
              organization?.initialIntegrationType === "hubspot",
              savingLicenseIds
            )}
              getRowId={(row) => row.id.toString()}
            />
          </div>
        </Grid>
      </Card>
      )}

      {/* Add licenses (self-serve seat purchase) */}
      <AddLicensesDialog
        open={isAddLicensesOpen}
        onClose={() => setIsAddLicensesOpen(false)}
      />

      {/* Sync Licenses Dialog */}
      <Dialog
        open={isSyncDialogOpen}
        maxWidth={600}
        onClose={() => {
          setIsSyncDialogOpen(false);
          setExpandedAddingUsers(false);
          setExpandedRemovingUsers(false);
          setUseSavedConfig(true); // Reset to saved config on close
          setSelectedTeam("");
          setSelectedRole("");
          setSelectedTeams([]);
          setSelectedRoles([]);
          setTeamSelectValue("");
          setRoleSelectValue("");
          setSyncConfigAnchorEl(null);
          setSyncConfigAnchorPosition(null);
          setSyncConfigSearchQuery("");
        }}
        title={`Sync boring & ${organization?.initialIntegrationType === "hubspot" ? "HubSpot" : "Outreach"} licenses`}
        primaryButtonProps={{
          onClick: handleSyncLicenses,
          children: "Sync",
          loading: isSyncing,
          variant: ButtonVariants.brand,
          disabled: isSyncing || (!useSavedConfig && !(organization?.licenseSyncTeams && organization.licenseSyncTeams.length > 0 || organization?.licenseSyncRoles && organization.licenseSyncRoles.length > 0) && selectedTeams.length === 0 && selectedRoles.length === 0),
        }}
        secondaryButtonProps={{
          onClick: () => {
            setIsSyncDialogOpen(false);
            setUseSavedConfig(true);
          },
          children: "Cancel",
          variant: ButtonVariants.light,
          disabled: isSyncing,
          className: "sync-dialog-cancel-btn",
        }}
      >
        <Grid container direction="column" gap={GAPS.sm}>
          {/* Check if saved config exists */}
          {organization?.licenseSyncTeams && organization.licenseSyncTeams.length > 0 || organization?.licenseSyncRoles && organization.licenseSyncRoles.length > 0 ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                <Checkbox
                  checked={useSavedConfig}
                  onChange={(e) => {
                    setUseSavedConfig(e.target.checked);
                    if (e.target.checked) {
                      // Reset manual selections when using saved config
                      setSelectedTeam("");
                      setSelectedRole("");
                      setSelectedTeams([]);
                      setSelectedRoles([]);
                    }
                  }}
                  sx={{
                    color: COLORS.foregroundBrand,
                    "&.Mui-checked": {
                      color: COLORS.foregroundBrand,
                    },
                    padding: "4px",
                  }}
                />
                <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                  Use saved configuration
                </Text>
              </Box>
              {useSavedConfig && (
                <>
                  <Text 
                    variant={TextVariant.sm} 
                    style={{ 
                      color: "var(--color-foregroundSecondary, #686781)",
                      lineHeight: "1.75",
                      marginBottom: SPACINGS.md,
                    }}
                  >
                    This will automatically assign boring licenses to all active Outreach users from the selected teams/profiles who don&apos;t already have one. Any time you perform a sync, it automatically removes boring licenses from all inactive users and users who moved to non-synced teams/profiles.
                  </Text>
                  
                  {/* Saved Configuration Display - Chip Box */}
                  <Box sx={{ marginBottom: SPACINGS.lg }}>
                    <Box
                      sx={{
                        minHeight: "56px",
                        width: "100%",
                        border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
                        borderRadius: RADII.md,
                        padding: SPACINGS.sm,
                        backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: SPACINGS.xs,
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Team Chips from saved config */}
                      {organization.licenseSyncTeams && organization.licenseSyncTeams.map((team) => (
                        <Chip
                          key={`saved-team-${team}`}
                          label={team}
                          sx={{
                            backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                            color: "var(--color-foregroundBrand, #5e5eaf)",
                            border: "1px solid var(--color-chipBorder, rgba(89, 81, 255, 0.24)) !important",
                            borderRadius: "20px",
                            height: "32px",
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            fontWeight: FONT_WEIGHTS.light,
                            fontSize: "0.875rem",
                            "& .MuiChip-label": {
                              color: "var(--color-foregroundBrand, #5e5eaf)",
                              fontWeight: FONT_WEIGHTS.light,
                              paddingLeft: "0px",
                              paddingRight: "0px",
                            },
                          }}
                        />
                      ))}
                      
                      {/* Role Chips from saved config */}
                      {organization.licenseSyncRoles && organization.licenseSyncRoles.map((role) => (
                        <Chip
                          key={`saved-role-${role}`}
                          label={role}
                          sx={{
                            backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                            color: "var(--color-foregroundBrand, #5e5eaf)",
                            border: "1px solid var(--color-chipBorder, rgba(89, 81, 255, 0.24)) !important",
                            borderRadius: "20px",
                            height: "32px",
                            paddingLeft: "12px",
                            paddingRight: "12px",
                            fontWeight: FONT_WEIGHTS.light,
                            fontSize: "0.875rem",
                            "& .MuiChip-label": {
                              color: "var(--color-foregroundBrand, #5e5eaf)",
                              fontWeight: FONT_WEIGHTS.light,
                              paddingLeft: "0px",
                              paddingRight: "0px",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              {/* No saved config - show chip-based configuration UI */}
              <Text 
                variant={TextVariant.sm} 
                style={{ 
                  color: "var(--color-foregroundSecondary, #686781)",
                  lineHeight: "1.75",
                  marginBottom: SPACINGS.md,
                }}
              >
                Configure which teams and profiles should automatically receive boring licenses. This configuration will be saved for future syncs.
              </Text>
              
              {/* Combined Teams and Profiles Configuration - Chip Box */}
              <Box sx={{ marginBottom: SPACINGS.lg }}>
                <Box
                  data-sync-config-box
                  sx={{
                    minHeight: "56px",
                    width: "100%",
                    border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
                    borderRadius: RADII.md,
                    padding: SPACINGS.sm,
                    backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: SPACINGS.xs,
                    alignItems: "flex-start",
                    cursor: "text",
                    "&:hover": {
                      borderColor: COLORS.foregroundBrand,
                    },
                    "&:focus-within": {
                      borderColor: COLORS.foregroundBrand,
                      borderWidth: "2px",
                    },
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'INPUT') {
                      setSyncConfigAnchorPosition({
                        top: e.clientY,
                        left: e.clientX,
                      });
                      setSyncConfigAnchorEl(e.currentTarget);
                    }
                  }}
                >
                  {/* Team Chips */}
                  {selectedTeams.map((team) => (
                    <Chip
                      key={`team-${team}`}
                      label={team}
                      onDelete={() => {
                        setSelectedTeams(selectedTeams.filter((t) => t !== team));
                      }}
                      deleteIcon={
                        <svg
                          viewBox="0 0 24 24"
                          focusable="false"
                          aria-hidden="true"
                          data-testid="CancelIcon"
                          style={{ width: "14px", height: "14px" }}
                        >
                          <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                          <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                        </svg>
                      }
                      sx={{
                        backgroundColor: "#ffffff",
                        color: "#5e5eaf",
                        border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        borderRadius: "20px",
                        height: "32px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        fontWeight: FONT_WEIGHTS.light,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f9f9fb",
                          border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        },
                        "& .MuiChip-label": {
                          color: "#5e5eaf",
                          fontWeight: FONT_WEIGHTS.light,
                          paddingLeft: "0px",
                          paddingRight: "0px",
                        },
                        "& .MuiChip-deleteIcon": {
                          width: "14px !important",
                          height: "14px !important",
                          marginRight: "0px",
                          marginLeft: "8px",
                          padding: "0 !important",
                          "& svg": {
                            width: "14px !important",
                            height: "14px !important",
                          },
                          "& circle": {
                            fill: "#5e5eaf !important",
                          },
                          "& path": {
                            fill: "#ffffff !important",
                          },
                          "&:hover": {
                            "& circle": {
                              fill: "#4a4a9f !important",
                            },
                          },
                        },
                      }}
                    />
                  ))}
                  
                  {/* Role Chips */}
                  {selectedRoles.map((role) => (
                    <Chip
                      key={`role-${role}`}
                      label={role}
                      onDelete={() => {
                        setSelectedRoles(selectedRoles.filter((r) => r !== role));
                      }}
                      deleteIcon={
                        <svg
                          viewBox="0 0 24 24"
                          focusable="false"
                          aria-hidden="true"
                          data-testid="CancelIcon"
                          style={{ width: "14px", height: "14px" }}
                        >
                          <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                          <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                        </svg>
                      }
                      sx={{
                        backgroundColor: "#ffffff",
                        color: "#5e5eaf",
                        border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        borderRadius: "20px",
                        height: "32px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        fontWeight: FONT_WEIGHTS.light,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f9f9fb",
                          border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        },
                        "& .MuiChip-label": {
                          color: "#5e5eaf",
                          fontWeight: FONT_WEIGHTS.light,
                          paddingLeft: "0px",
                          paddingRight: "0px",
                        },
                        "& .MuiChip-deleteIcon": {
                          width: "14px !important",
                          height: "14px !important",
                          marginRight: "0px",
                          marginLeft: "8px",
                          padding: "0 !important",
                          "& svg": {
                            width: "14px !important",
                            height: "14px !important",
                          },
                          "& circle": {
                            fill: "#5e5eaf !important",
                          },
                          "& path": {
                            fill: "#ffffff !important",
                          },
                          "&:hover": {
                            "& circle": {
                              fill: "#4a4a9f !important",
                            },
                          },
                        },
                      }}
                    />
                  ))}
                  
                  {/* Add Button/Input - Only show when no items are selected */}
                  {selectedTeams.length === 0 && selectedRoles.length === 0 && (
                    <Box
                      onClick={(e) => {
                        // Only handle clicks directly on the Box, not on the input (input handles its own clicks)
                        if (e.target === e.currentTarget) {
                          setSyncConfigAnchorPosition({
                            top: e.clientY,
                            left: e.clientX,
                          });
                          setSyncConfigAnchorEl(e.currentTarget.parentElement as HTMLDivElement);
                        }
                      }}
                      sx={{
                        minWidth: "200px",
                        flex: 1,
                        cursor: "text",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Add teams or profiles..."
                        value=""
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent Box onClick from interfering
                          // Set position at cursor click location
                          setSyncConfigAnchorPosition({
                            top: e.clientY,
                            left: e.clientX,
                          });
                          const boxElement = e.currentTarget.closest('[data-sync-config-box]') as HTMLDivElement;
                          if (boxElement) {
                            setSyncConfigAnchorEl(boxElement);
                          }
                        }}
                        onFocus={(e) => {
                          const boxElement = e.currentTarget.closest('[data-sync-config-box]') as HTMLDivElement;
                          if (boxElement && !syncConfigAnchorEl) {
                            // Only open if not already open, but don't override click position
                            // If no click position was set, use default anchor positioning
                            setSyncConfigAnchorEl(boxElement);
                          }
                        }}
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          backgroundColor: "transparent",
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                          fontFamily: FONT_FAMILIES.regular,
                          color: "#686783",
                        }}
                        className="sync-config-input"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Searchable Dropdown Popover */}
              <Popover
                open={Boolean(syncConfigAnchorEl)}
                anchorEl={syncConfigAnchorEl}
                anchorPosition={syncConfigAnchorPosition || undefined}
                anchorReference={syncConfigAnchorPosition ? "anchorPosition" : "anchorEl"}
                onClose={() => {
                  setSyncConfigAnchorEl(null);
                  setSyncConfigAnchorPosition(null);
                  setSyncConfigSearchQuery("");
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    marginTop: "8px",
                    borderRadius: RADII.md,
                    minWidth: "400px",
                    maxWidth: "500px",
                    maxHeight: "400px",
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
                    backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                    border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
                  },
                  onClick: (e: React.MouseEvent) => {
                    // Prevent closing when clicking inside the popover
                    e.stopPropagation();
                  },
                }}
                onKeyDown={(e) => {
                  // Close on Escape key
                  if (e.key === "Escape") {
                    setSyncConfigAnchorEl(null);
                    setSyncConfigAnchorPosition(null);
                    setSyncConfigSearchQuery("");
                  }
                }}
                disableRestoreFocus
              >
                <Paper sx={{ padding: SPACINGS.sm, boxShadow: "none", backgroundColor: COLORS.backgroundWhite }}>
                  {/* Search Input */}
                  <Box sx={{ marginBottom: SPACINGS.sm }}>
                    {isHubSpotLook ? (
                      <OuSearch value={syncConfigSearchQuery} onChange={setSyncConfigSearchQuery} placeholder="Search teams and profiles" width="100%" autoFocus />
                    ) : (
                    <Input
                      type="text"
                      placeholder="Search teams and profiles..."
                      value={syncConfigSearchQuery}
                      onChange={(e) => setSyncConfigSearchQuery(e.target.value)}
                      autoFocus
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <div style={{ marginRight: "8px", display: "flex", alignItems: "center", paddingLeft: "8px" }}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              style={{ color: COLORS.foregroundSecondary }}
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L19.2928 20.7071C19.6833 21.0977 20.3165 21.0977 20.707 20.7071C21.0975 20.3166 21.0975 19.6835 20.707 19.2929L16.3199 14.9057C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2ZM4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        ),
                      }}
                      inputProps={{
                        style: { paddingLeft: "0px" },
                      }}
                    />
                    )}
                  </Box>
                  
                  {/* Teams Section */}
                  {availableTeams.filter((team) => 
                    !selectedTeams.includes(team) && 
                    team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length > 0 && (
                    <Box sx={{ marginBottom: SPACINGS.md }}>
                      <Text variant={TextVariant.sm} style={{ 
                        color: "var(--color-foregroundSecondary, #686781)",
                        fontWeight: FONT_WEIGHTS.medium,
                        marginBottom: SPACINGS.xs,
                        paddingLeft: SPACINGS.xs,
                      }}>
                        Teams
                      </Text>
                      <List sx={{ padding: 0, maxHeight: "200px", overflowY: "auto" }}>
                        {availableTeams
                          .filter((team) => 
                            !selectedTeams.includes(team) && 
                            team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                          )
                          .map((team) => (
                            <ListItem key={team} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  if (!selectedTeams.includes(team)) {
                                    setSelectedTeams([...selectedTeams, team]);
                                  }
                                  setSyncConfigSearchQuery("");
                                  setSyncConfigAnchorEl(null);
                                setSyncConfigAnchorPosition(null);
                                }}
                                sx={{
                                  width: "100%",
                                  padding: "8px 16px",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: COLORS.backgroundSecondary,
                                  },
                                }}
                              >
                                <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                                  {team}
                                </Text>
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Profiles Section */}
                  {availableRoles.filter((role) => 
                    !selectedRoles.includes(role) && 
                    role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length > 0 && (
                    <Box>
                      <Text variant={TextVariant.sm} style={{ 
                        color: "var(--color-foregroundSecondary, #686781)",
                        fontWeight: FONT_WEIGHTS.medium,
                        marginBottom: SPACINGS.xs,
                        paddingLeft: SPACINGS.xs,
                      }}>
                        Profiles
                      </Text>
                      <List sx={{ padding: 0, maxHeight: "200px", overflowY: "auto" }}>
                        {availableRoles
                          .filter((role) => 
                            !selectedRoles.includes(role) && 
                            role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                          )
                          .map((role) => (
                            <ListItem key={role} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  if (!selectedRoles.includes(role)) {
                                    setSelectedRoles([...selectedRoles, role]);
                                  }
                                  setSyncConfigSearchQuery("");
                                  setSyncConfigAnchorEl(null);
                                setSyncConfigAnchorPosition(null);
                                }}
                                sx={{
                                  width: "100%",
                                  padding: "8px 16px",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: COLORS.backgroundSecondary,
                                  },
                                }}
                              >
                                <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                                  {role}
                                </Text>
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* No Results */}
                  {availableTeams.filter((team) => 
                    !selectedTeams.includes(team) && 
                    team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length === 0 && 
                  availableRoles.filter((role) => 
                    !selectedRoles.includes(role) && 
                    role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length === 0 && 
                  syncConfigSearchQuery && (
                    <Box sx={{ padding: SPACINGS.md, textAlign: "center" }}>
                      <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundSecondary }}>
                        No teams or profiles found
                      </Text>
                    </Box>
                  )}
                </Paper>
              </Popover>
            </>
          )}
          
          {!useSavedConfig && (
            <>
              <Text 
                variant={TextVariant.sm} 
                style={{ 
                  color: "var(--color-foregroundSecondary, #686781)",
                  lineHeight: "1.75",
                  marginBottom: SPACINGS.md,
                }}
              >
                This will automatically assign boring licenses to all active Outreach users from the selected teams/profiles who don&apos;t already have one. Any time you perform a sync, it automatically removes boring licenses from all inactive users and users who moved to non-synced teams/profiles.
              </Text>
          
              {/* Combined Teams and Profiles Configuration - Chip Box */}
              <Box sx={{ marginBottom: SPACINGS.lg }}>
                <Box
                  data-sync-config-box
                  sx={{
                    minHeight: "56px",
                    width: "100%",
                    border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
                    borderRadius: RADII.md,
                    padding: SPACINGS.sm,
                    backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: SPACINGS.xs,
                    alignItems: "flex-start",
                    cursor: "text",
                    "&:hover": {
                      borderColor: COLORS.foregroundBrand,
                    },
                    "&:focus-within": {
                      borderColor: COLORS.foregroundBrand,
                      borderWidth: "2px",
                    },
                  }}
                  onClick={(e) => {
                    if (e.target === e.currentTarget || (e.target as HTMLElement).tagName === 'INPUT') {
                      setSyncConfigAnchorPosition({
                        top: e.clientY,
                        left: e.clientX,
                      });
                      setSyncConfigAnchorEl(e.currentTarget);
                    }
                  }}
                >
                  {/* Team Chips */}
                  {selectedTeams.map((team) => (
                    <Chip
                      key={`team-${team}`}
                      label={team}
                      onDelete={() => {
                        setSelectedTeams(selectedTeams.filter((t) => t !== team));
                        setSelectedTeam("");
                        setExpandedAddingUsers(false);
                        setExpandedRemovingUsers(false);
                      }}
                      deleteIcon={
                        <svg
                          viewBox="0 0 24 24"
                          focusable="false"
                          aria-hidden="true"
                          data-testid="CancelIcon"
                          style={{ width: "14px", height: "14px" }}
                        >
                          <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                          <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                        </svg>
                      }
                      sx={{
                        backgroundColor: "#ffffff",
                        color: "#5e5eaf",
                        border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        borderRadius: "20px",
                        height: "32px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        fontWeight: FONT_WEIGHTS.light,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f9f9fb",
                          border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        },
                        "& .MuiChip-label": {
                          color: "#5e5eaf",
                          fontWeight: FONT_WEIGHTS.light,
                          paddingLeft: "0px",
                          paddingRight: "0px",
                        },
                        "& .MuiChip-deleteIcon": {
                          width: "14px !important",
                          height: "14px !important",
                          marginRight: "0px",
                          marginLeft: "8px",
                          padding: "0 !important",
                          "& svg": {
                            width: "14px !important",
                            height: "14px !important",
                          },
                          "& circle": {
                            fill: "#5e5eaf !important",
                          },
                          "& path": {
                            fill: "#ffffff !important",
                          },
                          "&:hover": {
                            "& circle": {
                              fill: "#4a4a9f !important",
                            },
                          },
                        },
                      }}
                    />
                  ))}
                  
                  {/* Role Chips */}
                  {selectedRoles.map((role) => (
                    <Chip
                      key={`role-${role}`}
                      label={role}
                      onDelete={() => {
                        setSelectedRoles(selectedRoles.filter((r) => r !== role));
                        setSelectedRole("");
                        setExpandedAddingUsers(false);
                        setExpandedRemovingUsers(false);
                      }}
                      deleteIcon={
                        <svg
                          viewBox="0 0 24 24"
                          focusable="false"
                          aria-hidden="true"
                          data-testid="CancelIcon"
                          style={{ width: "14px", height: "14px" }}
                        >
                          <circle cx="12" cy="12" r="10" fill="#5e5eaf" />
                          <path d="M15.59 7L17 8.41L13.41 12L17 15.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7Z" fill="#ffffff" />
                        </svg>
                      }
                      sx={{
                        backgroundColor: "#ffffff",
                        color: "#5e5eaf",
                        border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        borderRadius: "20px",
                        height: "32px",
                        paddingLeft: "12px",
                        paddingRight: "12px",
                        fontWeight: FONT_WEIGHTS.light,
                        fontSize: "0.875rem",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f9f9fb",
                          border: "1px solid rgba(89, 81, 255, 0.24) !important",
                        },
                        "& .MuiChip-label": {
                          color: "#5e5eaf",
                          fontWeight: FONT_WEIGHTS.light,
                          paddingLeft: "0px",
                          paddingRight: "0px",
                        },
                        "& .MuiChip-deleteIcon": {
                          width: "14px !important",
                          height: "14px !important",
                          marginRight: "0px",
                          marginLeft: "8px",
                          padding: "0 !important",
                          "& svg": {
                            width: "14px !important",
                            height: "14px !important",
                          },
                          "& circle": {
                            fill: "#5e5eaf !important",
                          },
                          "& path": {
                            fill: "#ffffff !important",
                          },
                          "&:hover": {
                            "& circle": {
                              fill: "#4a4a9f !important",
                            },
                          },
                        },
                      }}
                    />
                  ))}
                  
                  {/* Add Button/Input - Only show when no items are selected */}
                  {selectedTeams.length === 0 && selectedRoles.length === 0 && (
                    <Box
                      onClick={(e) => {
                        // Only handle clicks directly on the Box, not on the input (input handles its own clicks)
                        if (e.target === e.currentTarget) {
                          setSyncConfigAnchorPosition({
                            top: e.clientY,
                            left: e.clientX,
                          });
                          setSyncConfigAnchorEl(e.currentTarget.parentElement as HTMLDivElement);
                        }
                      }}
                      sx={{
                        minWidth: "200px",
                        flex: 1,
                        cursor: "text",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Add teams or profiles..."
                        value=""
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent Box onClick from interfering
                          // Set position at cursor click location
                          setSyncConfigAnchorPosition({
                            top: e.clientY,
                            left: e.clientX,
                          });
                          const boxElement = e.currentTarget.closest('[data-sync-config-box]') as HTMLDivElement;
                          if (boxElement) {
                            setSyncConfigAnchorEl(boxElement);
                          }
                        }}
                        onFocus={(e) => {
                          const boxElement = e.currentTarget.closest('[data-sync-config-box]') as HTMLDivElement;
                          if (boxElement && !syncConfigAnchorEl) {
                            // Only open if not already open, but don't override click position
                            // If no click position was set, use default anchor positioning
                            setSyncConfigAnchorEl(boxElement);
                          }
                        }}
                        style={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          backgroundColor: "transparent",
                          padding: "8px 12px",
                          fontSize: "0.875rem",
                          fontFamily: FONT_FAMILIES.regular,
                          color: "#686783",
                        }}
                        className="sync-config-input"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Searchable Dropdown Popover */}
              <Popover
                open={Boolean(syncConfigAnchorEl)}
                anchorEl={syncConfigAnchorEl}
                anchorPosition={syncConfigAnchorPosition || undefined}
                anchorReference={syncConfigAnchorPosition ? "anchorPosition" : "anchorEl"}
                onClose={() => {
                  setSyncConfigAnchorEl(null);
                  setSyncConfigAnchorPosition(null);
                  setSyncConfigSearchQuery("");
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                PaperProps={{
                  sx: {
                    marginTop: "8px",
                    borderRadius: RADII.md,
                    minWidth: "400px",
                    maxWidth: "500px",
                    maxHeight: "400px",
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
                    backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                    border: `1px solid var(--color-borderTertiary, ${COLORS.borderTertiary})`,
                  },
                  onClick: (e: React.MouseEvent) => {
                    // Prevent closing when clicking inside the popover
                    e.stopPropagation();
                  },
                }}
                onKeyDown={(e) => {
                  // Close on Escape key
                  if (e.key === "Escape") {
                    setSyncConfigAnchorEl(null);
                    setSyncConfigAnchorPosition(null);
                    setSyncConfigSearchQuery("");
                  }
                }}
                disableRestoreFocus
              >
                <Paper sx={{ padding: SPACINGS.sm, boxShadow: "none", backgroundColor: COLORS.backgroundWhite }}>
                  {/* Search Input */}
                  <Box sx={{ marginBottom: SPACINGS.sm }}>
                    {isHubSpotLook ? (
                      <OuSearch value={syncConfigSearchQuery} onChange={setSyncConfigSearchQuery} placeholder="Search teams and profiles" width="100%" autoFocus />
                    ) : (
                    <Input
                      type="text"
                      placeholder="Search teams and profiles..."
                      value={syncConfigSearchQuery}
                      onChange={(e) => setSyncConfigSearchQuery(e.target.value)}
                      autoFocus
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <div style={{ marginRight: "8px", display: "flex", alignItems: "center", paddingLeft: "8px" }}>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              style={{ color: COLORS.foregroundSecondary }}
                            >
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18C11.8487 18 13.551 17.3729 14.9056 16.3199L19.2928 20.7071C19.6833 21.0977 20.3165 21.0977 20.707 20.7071C21.0975 20.3166 21.0975 19.6835 20.707 19.2929L16.3199 14.9057C17.3729 13.551 18 11.8487 18 10C18 5.58172 14.4183 2 10 2ZM4 10C4 6.68629 6.68629 4 10 4C13.3137 4 16 6.68629 16 10C16 13.3137 13.3137 16 10 16C6.68629 16 4 13.3137 4 10Z"
                                fill="currentColor"
                              />
                            </svg>
                          </div>
                        ),
                      }}
                      inputProps={{
                        style: { paddingLeft: "0px" },
                      }}
                    />
                    )}
                  </Box>
                  
                  {/* Teams Section */}
                  {availableTeams.filter((team) => 
                    !selectedTeams.includes(team) && 
                    team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length > 0 && (
                    <Box sx={{ marginBottom: SPACINGS.md }}>
                      <Text variant={TextVariant.sm} style={{ 
                        color: "var(--color-foregroundSecondary, #686781)",
                        fontWeight: FONT_WEIGHTS.medium,
                        marginBottom: SPACINGS.xs,
                        paddingLeft: SPACINGS.xs,
                      }}>
                        Teams
                      </Text>
                      <List sx={{ padding: 0, maxHeight: "200px", overflowY: "auto" }}>
                        {availableTeams
                          .filter((team) => 
                            !selectedTeams.includes(team) && 
                            team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                          )
                          .map((team) => (
                            <ListItem key={team} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  if (!selectedTeams.includes(team)) {
                                    setSelectedTeams([...selectedTeams, team]);
                                    setSelectedTeam("");
                                    setExpandedAddingUsers(false);
                                    setExpandedRemovingUsers(false);
                                  }
                                  setSyncConfigSearchQuery("");
                                  setSyncConfigAnchorEl(null);
                                  setSyncConfigAnchorPosition(null);
                                }}
                                sx={{
                                  width: "100%",
                                  padding: "8px 16px",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: COLORS.backgroundSecondary,
                                  },
                                }}
                              >
                                <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                                  {team}
                                </Text>
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* Profiles Section */}
                  {availableRoles.filter((role) => 
                    !selectedRoles.includes(role) && 
                    role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length > 0 && (
                    <Box sx={{ marginBottom: SPACINGS.md }}>
                      <Text variant={TextVariant.sm} style={{ 
                        color: "var(--color-foregroundSecondary, #686781)",
                        fontWeight: FONT_WEIGHTS.medium,
                        marginBottom: SPACINGS.xs,
                        paddingLeft: SPACINGS.xs,
                      }}>
                        Profiles
                      </Text>
                      <List sx={{ padding: 0, maxHeight: "200px", overflowY: "auto" }}>
                        {availableRoles
                          .filter((role) => 
                            !selectedRoles.includes(role) && 
                            role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                          )
                          .map((role) => (
                            <ListItem key={role} disablePadding>
                              <ListItemButton
                                onClick={() => {
                                  if (!selectedRoles.includes(role)) {
                                    setSelectedRoles([...selectedRoles, role]);
                                    setSelectedRole("");
                                    setExpandedAddingUsers(false);
                                    setExpandedRemovingUsers(false);
                                  }
                                  setSyncConfigSearchQuery("");
                                  setSyncConfigAnchorEl(null);
                                  setSyncConfigAnchorPosition(null);
                                }}
                                sx={{
                                  width: "100%",
                                  padding: "8px 16px",
                                  borderRadius: "6px",
                                  "&:hover": {
                                    backgroundColor: COLORS.backgroundSecondary,
                                  },
                                }}
                              >
                                <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                                  {role}
                                </Text>
                              </ListItemButton>
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  )}
                  
                  {/* No Results */}
                  {availableTeams.filter((team) => 
                    !selectedTeams.includes(team) && 
                    team.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length === 0 && 
                  availableRoles.filter((role) => 
                    !selectedRoles.includes(role) && 
                    role.toLowerCase().includes(syncConfigSearchQuery.toLowerCase())
                  ).length === 0 && 
                  syncConfigSearchQuery && (
                    <Box sx={{ padding: SPACINGS.md, textAlign: "center" }}>
                      <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundSecondary }}>
                        No teams or profiles found
                      </Text>
                    </Box>
                  )}
                </Paper>
              </Popover>
            </>
          )}
          
          {/* Preview for saved configuration */}
          {useSavedConfig && organization?.licenseSyncTeams && organization.licenseSyncTeams.length > 0 || organization?.licenseSyncRoles && organization.licenseSyncRoles.length > 0 ? (() => {
            const savedTeams = organization?.licenseSyncTeams || [];
            const savedRoles = organization?.licenseSyncRoles || [];
            
            const usersToAddLicense = outreachUsers.filter((user) => {
              const isActive = user.isActive;
              const isNotWhitelisted = !user.isWhitelisted;
              
              // Check if user matches any of the saved teams
              let matchesTeam = false;
              if (savedTeams.length > 0) {
                const userTeams = user.teams || [];
                matchesTeam = userTeams.some(team => savedTeams.includes(team));
              }
              
              // Check if user matches any of the saved roles
              let matchesRole = false;
              if (savedRoles.length > 0) {
                matchesRole = savedRoles.includes(user.role || "");
              }
              
              // User matches if they match ANY configured team OR ANY configured role (OR logic)
              const matchesFilter = matchesTeam || matchesRole;
              
              return isActive && matchesFilter && isNotWhitelisted;
            });
            
            // Get users to remove licenses from:
            // 1. Inactive users who have licenses (regardless of team/role)
            const inactiveUsers = outreachUsers.filter((user) => {
              const isInactive = !user.isActive;
              const isWhitelisted = user.isWhitelisted;
              return isInactive && isWhitelisted;
            });

            // 2. Active users who moved to teams/roles not in the sync list
            // Only check if we have teams or roles configured (otherwise, sync would error out)
            const usersInWrongTeams = (savedTeams.length > 0 || savedRoles.length > 0) ? outreachUsers.filter((user) => {
              const isActive = user.isActive;
              const isWhitelisted = user.isWhitelisted;
              if (!isActive || !isWhitelisted) return false;
              
              const userTeams = user.teams || [];
              const userRole = user.role || "";
              
              // Check if user matches any configured team or role (OR logic)
              // Remove license only if user doesn't match ANY team AND doesn't match ANY role
              const isInSyncedTeam = savedTeams.length > 0 && userTeams.some(team => savedTeams.includes(team));
              const hasSyncedRole = savedRoles.length > 0 && savedRoles.includes(userRole);
              
              // User keeps license if they match ANY configured team OR role
              // Remove license if they match NEITHER
              return !isInSyncedTeam && !hasSyncedRole;
            }) : [];

            const usersToRemoveLicense = [...inactiveUsers, ...usersInWrongTeams];
            
            const totalChanges = usersToAddLicense.length + usersToRemoveLicense.length;
            
            return (
              <div style={{ display: "flex", gap: SPACINGS.lg }}>
                {/* Green box for adding licenses */}
                {usersToAddLicense.length > 0 && (
                  <div style={{
                    flex: 1,
                    padding: SPACINGS.lg,
                    backgroundColor: "var(--color-statusSuccessBg, #e7f3f2)",
                    borderRadius: RADII.md,
                    position: "relative",
                  }}>
                    <svg
                      style={{
                        position: "absolute",
                        top: SPACINGS.lg,
                        left: SPACINGS.lg,
                        width: "20px",
                        height: "20px",
                      }}
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z"
                        fill="rgb(17, 163, 137)"
                      />
                    </svg>
                    <div style={{ paddingLeft: "32px" }}>
                      <Text variant={TextVariant.sm} style={{ 
                        marginBottom: SPACINGS.xs, 
                        lineHeight: "1.34", 
                        fontWeight: 300, 
                        fontSize: "0.938rem",
                        fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        color: "var(--color-statusSuccessText, #02382d)" 
                      }}>
                        Assigning {usersToAddLicense.length} {usersToAddLicense.length === 1 ? "boring license" : "boring licenses"}
                      </Text>
                      <div style={{ maxHeight: "200px", overflowY: "auto", marginLeft: `-32px`, paddingLeft: `32px`, position: "relative", paddingBottom: usersToAddLicense.length > 2 ? "32px" : "0" }}>
                        {(expandedAddingUsers ? usersToAddLicense : usersToAddLicense.slice(0, 2)).map((user) => {
                          const initials = getUserInitials(user.fullName);
                          return (
                            <div 
                              key={user.id} 
                              style={{ 
                                marginTop: SPACINGS.sm,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#02382d",
                                  flexShrink: 0,
                                }}
                              >
                                {user.avatarUrl ? (
                                  <img
                                    src={user.avatarUrl}
                                    alt={user.fullName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span
                                    style={{
                                      color: "#FAFAFB",
                                      fontSize: "12px",
                                      fontWeight: FONT_WEIGHTS.medium,
                                      fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    }}
                                  >
                                    {initials}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                <div
                                  style={{
                                    fontWeight: FONT_WEIGHTS.medium,
                                    fontSize: "14px",
                                    color: "var(--color-statusSuccessText, #02382d)",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {user.fullName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "var(--color-statusSuccessText, #02382d)",
                                    lineHeight: "1.4",
                                    opacity: 0.8,
                                  }}
                                >
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {usersToAddLicense.length > 2 && !expandedAddingUsers && (
                          <Button
                            variant={ButtonVariants.light}
                            onClick={() => setExpandedAddingUsers(true)}
                            style={{
                              position: "absolute",
                              bottom: "8px",
                              right: "8px",
                              padding: "4px 8px",
                              fontSize: "12px",
                              minWidth: "auto",
                              backgroundColor: "transparent",
                              color: "var(--color-statusSuccessText, #02382d)",
                              border: "none",
                            }}
                          >
                            See more
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Red box for removing licenses */}
                {usersToRemoveLicense.length > 0 && (
                  <div style={{
                    flex: 1,
                    padding: SPACINGS.lg,
                    backgroundColor: "var(--color-statusDangerBg, #FCEEEF)",
                    borderRadius: RADII.md,
                    position: "relative",
                  }}>
                    <svg
                      style={{
                        position: "absolute",
                        top: SPACINGS.lg,
                        left: SPACINGS.lg,
                        width: "20px",
                        height: "20px",
                      }}
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
                        fill="var(--color-statusDangerIcon, rgb(198, 47, 76))"
                      />
                    </svg>
                    <div style={{ paddingLeft: "32px" }}>
                      <Text variant={TextVariant.sm} style={{ 
                        marginBottom: SPACINGS.xs, 
                        lineHeight: "1.34", 
                        fontWeight: 300, 
                        fontSize: "0.938rem",
                        fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        color: "var(--color-statusDangerText, #5f1221)" 
                      }}>
                        Removing {usersToRemoveLicense.length} {usersToRemoveLicense.length === 1 ? "license" : "licenses"}
                      </Text>
                      <div style={{ maxHeight: "200px", overflowY: "auto", marginLeft: `-32px`, paddingLeft: `32px`, position: "relative", paddingBottom: usersToRemoveLicense.length > 2 ? "32px" : "0" }}>
                        {(expandedRemovingUsers ? usersToRemoveLicense : usersToRemoveLicense.slice(0, 2)).map((user) => {
                          const initials = getUserInitials(user.fullName);
                          return (
                            <div 
                              key={user.id} 
                              style={{ 
                                marginTop: SPACINGS.sm,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                              }}
                            >
                              <div
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#5f1221",
                                  flexShrink: 0,
                                }}
                              >
                                {user.avatarUrl ? (
                                  <img
                                    src={user.avatarUrl}
                                    alt={user.fullName}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                    }}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span
                                    style={{
                                      color: "#FAFAFB",
                                      fontSize: "12px",
                                      fontWeight: FONT_WEIGHTS.medium,
                                      fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                    }}
                                  >
                                    {initials}
                                  </span>
                                )}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                <div
                                  style={{
                                    fontWeight: FONT_WEIGHTS.medium,
                                    fontSize: "14px",
                                    color: "var(--color-statusDangerText, #5f1221)",
                                    lineHeight: "1.4",
                                  }}
                                >
                                  {user.fullName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "var(--color-statusDangerText, #5f1221)",
                                    lineHeight: "1.4",
                                    opacity: 0.8,
                                  }}
                                >
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {usersToRemoveLicense.length > 2 && !expandedRemovingUsers && (
                          <Button
                            variant={ButtonVariants.light}
                            onClick={() => setExpandedRemovingUsers(true)}
                            style={{
                              position: "absolute",
                              bottom: "8px",
                              right: "8px",
                              padding: "4px 8px",
                              fontSize: "12px",
                              minWidth: "auto",
                              backgroundColor: "transparent",
                              color: "var(--color-statusDangerText, #5f1221)",
                              border: "none",
                            }}
                          >
                            See more
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {totalChanges === 0 && (
                  <div style={{
                    padding: SPACINGS.md,
                    backgroundColor: "var(--color-statusInfoBg, #e6efff)",
                    borderRadius: RADII.md,
                    width: "100%",
                  }}>
                    <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundSecondary, lineHeight: "1.75" }}>
                      No license changes needed. All active users from the selected teams/profiles already have licenses, and no licenses need to be revoked from inactive users or users in non-synced teams/profiles.
                    </Text>
                  </div>
                )}
              </div>
            );
          })() : null}
          
          {/* Team or Role Selector Content */}
          {!useSavedConfig && (selectedTeams.length > 0 || selectedRoles.length > 0) && (() => {
            const usersToAddLicense = outreachUsers.filter((user) => {
              const isActive = user.isActive;
              const isNotWhitelisted = !user.isWhitelisted;
              
              // Check if user matches any of the selected teams
              let matchesTeam = false;
              if (selectedTeams.length > 0) {
                const userTeams = user.teams || [];
                matchesTeam = userTeams.some(team => selectedTeams.includes(team));
              }
              
              // Check if user matches any of the selected roles
              let matchesRole = false;
              if (selectedRoles.length > 0) {
                matchesRole = selectedRoles.includes(user.role || "");
              }
              
              // User matches if they match ANY configured team OR ANY configured role (OR logic)
              const matchesFilter = matchesTeam || matchesRole;
              
              return isActive && matchesFilter && isNotWhitelisted;
            });
            
            // Get users to remove licenses from:
            // 1. Inactive users who have licenses (regardless of team/role)
            const inactiveUsers = outreachUsers.filter((user) => {
              const isInactive = !user.isActive;
              const isWhitelisted = user.isWhitelisted;
              return isInactive && isWhitelisted;
            });

            // 2. Active users who moved to teams/roles not in the sync list
            // Only check if we have teams or roles configured (otherwise, sync would error out)
            const usersInWrongTeams = (selectedTeams.length > 0 || selectedRoles.length > 0) ? outreachUsers.filter((user) => {
              const isActive = user.isActive;
              const isWhitelisted = user.isWhitelisted;
              if (!isActive || !isWhitelisted) return false;
              
              const userTeams = user.teams || [];
              const userRole = user.role || "";
              
              // Check if user matches any configured team or role (OR logic)
              // Remove license only if user doesn't match ANY team AND doesn't match ANY role
              const isInSyncedTeam = selectedTeams.length > 0 && userTeams.some(team => selectedTeams.includes(team));
              const hasSyncedRole = selectedRoles.length > 0 && selectedRoles.includes(userRole);
              
              // User keeps license if they match ANY configured team OR role
              // Remove license if they match NEITHER
              return !isInSyncedTeam && !hasSyncedRole;
            }) : [];

            const usersToRemoveLicense = [...inactiveUsers, ...usersInWrongTeams];
            
            const totalChanges = usersToAddLicense.length + usersToRemoveLicense.length;
                  
                  return (
                    <div style={{ display: "flex", gap: SPACINGS.lg }}>
                      {/* Green box for adding licenses */}
                      {usersToAddLicense.length > 0 && (
                        <div style={{
                          flex: 1,
                          padding: SPACINGS.lg,
                          backgroundColor: "var(--color-statusSuccessBg, #e7f3f2)",
                          borderRadius: RADII.md,
                          position: "relative",
                        }}>
                          <svg
                            style={{
                              position: "absolute",
                              top: SPACINGS.lg,
                              left: SPACINGS.lg,
                              width: "20px",
                              height: "20px",
                            }}
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z"
                              fill="rgb(17, 163, 137)"
                            />
                          </svg>
                          <div style={{ paddingLeft: "32px" }}>
                            <Text variant={TextVariant.sm} style={{ 
                              marginBottom: SPACINGS.xs, 
                              lineHeight: "1.34", 
                              fontWeight: 300, 
                              fontSize: "0.938rem",
                              fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                              color: "var(--color-statusSuccessText, #02382d)" 
                            }}>
                              Assigning {usersToAddLicense.length} {usersToAddLicense.length === 1 ? "boring license" : "boring licenses"}
                            </Text>
                            <div style={{ maxHeight: "200px", overflowY: "auto", marginLeft: `-32px`, paddingLeft: `32px`, position: "relative", paddingBottom: usersToAddLicense.length > 2 ? "32px" : "0" }}>
                              {(expandedAddingUsers ? usersToAddLicense : usersToAddLicense.slice(0, 2)).map((user) => {
                                const initials = getUserInitials(user.fullName);
                                return (
                                  <div 
                                    key={user.id} 
                                    style={{ 
                                      marginTop: SPACINGS.sm,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.75rem",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#02382d",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {user.avatarUrl ? (
                                        <img
                                          src={user.avatarUrl}
                                          alt={user.fullName}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = "none";
                                          }}
                                        />
                                      ) : (
                                        <span
                                          style={{
                                            color: "#FAFAFB",
                                            fontSize: "12px",
                                            fontWeight: FONT_WEIGHTS.medium,
                                            fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                          }}
                                        >
                                          {initials}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                      <div
                                        style={{
                                          fontWeight: FONT_WEIGHTS.medium,
                                          fontSize: "14px",
                                          color: "var(--color-statusSuccessText, #02382d)",
                                          lineHeight: "1.4",
                                        }}
                                      >
                                        {user.fullName}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color: "var(--color-statusSuccessText, #02382d)",
                                          lineHeight: "1.4",
                                          opacity: 0.8,
                                        }}
                                      >
                                        {user.email}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {usersToAddLicense.length > 2 && !expandedAddingUsers && (
                                <Button
                                  variant={ButtonVariants.light}
                                  onClick={() => setExpandedAddingUsers(true)}
                                  style={{
                                    position: "absolute",
                                    bottom: "8px",
                                    right: "8px",
                                    padding: "4px 8px",
                                    fontSize: "12px",
                                    minWidth: "auto",
                                    backgroundColor: "transparent",
                                    color: "var(--color-statusSuccessText, #02382d)",
                                    border: "none",
                                  }}
                                >
                                  See more
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Red box for removing licenses */}
                      {usersToRemoveLicense.length > 0 && (
                        <div style={{
                          flex: 1,
                          padding: SPACINGS.lg,
                          backgroundColor: "var(--color-statusDangerBg, #FCEEEF)",
                          borderRadius: RADII.md,
                          position: "relative",
                        }}>
                          <svg
                            style={{
                              position: "absolute",
                              top: SPACINGS.lg,
                              left: SPACINGS.lg,
                              width: "20px",
                              height: "20px",
                            }}
                            viewBox="0 0 24 24"
                            fill="none"
                          >
                            <path
                              d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"
                              fill="var(--color-statusDangerIcon, rgb(198, 47, 76))"
                            />
                          </svg>
                          <div style={{ paddingLeft: "32px" }}>
                            <Text variant={TextVariant.sm} style={{ 
                              marginBottom: SPACINGS.xs, 
                              lineHeight: "1.34", 
                              fontWeight: 300, 
                              fontSize: "0.938rem",
                              fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                              color: "var(--color-statusDangerText, #5f1221)" 
                            }}>
                              Removing {usersToRemoveLicense.length} {usersToRemoveLicense.length === 1 ? "license" : "licenses"}
                            </Text>
                            <div style={{ maxHeight: "200px", overflowY: "auto", marginLeft: `-32px`, paddingLeft: `32px`, position: "relative", paddingBottom: usersToRemoveLicense.length > 2 ? "32px" : "0" }}>
                              {(expandedRemovingUsers ? usersToRemoveLicense : usersToRemoveLicense.slice(0, 2)).map((user) => {
                                const initials = getUserInitials(user.fullName);
                                return (
                                  <div 
                                    key={user.id} 
                                    style={{ 
                                      marginTop: SPACINGS.sm,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "0.75rem",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        borderRadius: "50%",
                                        overflow: "hidden",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#5f1221",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {user.avatarUrl ? (
                                        <img
                                          src={user.avatarUrl}
                                          alt={user.fullName}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                          }}
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = "none";
                                          }}
                                        />
                                      ) : (
                                        <span
                                          style={{
                                            color: "#FAFAFB",
                                            fontSize: "12px",
                                            fontWeight: FONT_WEIGHTS.medium,
                                            fontFamily: '"Roboto Flex", Graphik, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                          }}
                                        >
                                          {initials}
                                        </span>
                                      )}
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                      <div
                                        style={{
                                          fontWeight: FONT_WEIGHTS.medium,
                                          fontSize: "14px",
                                          color: "var(--color-statusDangerText, #5f1221)",
                                          lineHeight: "1.4",
                                        }}
                                      >
                                        {user.fullName}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color: "var(--color-statusDangerText, #5f1221)",
                                          lineHeight: "1.4",
                                          opacity: 0.8,
                                        }}
                                      >
                                        {user.email}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              {usersToRemoveLicense.length > 2 && !expandedRemovingUsers && (
                                <Button
                                  variant={ButtonVariants.light}
                                  onClick={() => setExpandedRemovingUsers(true)}
                                  style={{
                                    position: "absolute",
                                    bottom: "8px",
                                    right: "8px",
                                    padding: "4px 8px",
                                    fontSize: "12px",
                                    minWidth: "auto",
                                    backgroundColor: "transparent",
                                    color: "var(--color-statusDangerText, #5f1221)",
                                    border: "none",
                                  }}
                                >
                                  See more
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                {totalChanges === 0 && (
                  <div style={{
                    padding: SPACINGS.md,
                    backgroundColor: "var(--color-statusInfoBg, #e6efff)",
                    borderRadius: RADII.md,
                    width: "100%",
                  }}>
                    <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundSecondary, lineHeight: "1.75" }}>
                      No license changes needed. All active users from the selected teams/profiles already have licenses, and no licenses need to be revoked from inactive users or users in non-synced teams/profiles.
                    </Text>
                  </div>
                )}
              </div>
            );
          })()}
        </Grid>
      </Dialog>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => {
          setFilterAnchorEl(null);
          setFilterSearchQuery("");
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          className: "filter-popover-paper",
          sx: {
            marginTop: "8px",
            borderRadius: "8px",
            minWidth: "280px",
            maxWidth: "400px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
            backgroundColor: "#f9f9fb",
          },
          style: {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
            backgroundColor: "#f9f9fb",
          },
        }}
      >
        <Paper sx={{ padding: "8px", boxShadow: "none", backgroundColor: "#f9f9fb" }}>
          {/* Filter Options List */}
          <List sx={{ padding: 0, maxHeight: "300px", overflowY: "auto" }}>
            {[
              { id: "status", label: organization?.initialIntegrationType === "hubspot" ? "HubSpot user status" : "Outreach user status", type: "status" as const },
              { id: "boringLicense", label: "Boring license", type: "boringLicense" as const },
              ...(availableRoles.length > 0
                ? [{ id: "role", label: "Profile", type: "role" as const }]
                : []),
              ...(availableTeams.length > 0
                ? [{ id: "team", label: "Team", type: "team" as const }]
                : []),
            ].map((option) => (
                <ListItem key={option.id} disablePadding sx={{ width: "100%" }}>
                  <ListItemButton
                    onClick={(e) => {
                      setSelectedFilterType(option.type);
                      setFilterOperator("is");
                      setFilterValue("");
                      setFilterConfigAnchorEl(filterAnchorEl);
                      setFilterAnchorEl(null);
                      setFilterSearchQuery("");
                    }}
                    sx={{
                      padding: "8px 16px",
                      width: "100%",
                      margin: 0,
                      borderRadius: "6px",
                      "&:hover": {
                        backgroundColor: "var(--color-backgroundBrandTabHover, #f0eff2)",
                      },
                    }}
                  >
                    <Text variant={TextVariant.sm} style={{ color: COLORS.foregroundPrimary }}>
                      {option.label}
                    </Text>
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
        </Paper>
      </Popover>

      {/* Filter Configuration Popover */}
      <Popover
        open={selectedFilterType !== null && filterConfigAnchorEl !== null}
        anchorEl={filterConfigAnchorEl}
        onClose={() => {
          setSelectedFilterType(null);
          setFilterOperator("is");
          setFilterValue("");
          setFilterConfigAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          className: "filter-config-popover-paper",
          sx: {
            marginTop: "8px",
            borderRadius: "8px",
            minWidth: "400px",
            maxWidth: "400px",
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
            backgroundColor: "#f9f9fb",
          },
          style: {
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1) !important",
            backgroundColor: "#f9f9fb",
          },
        }}
      >
        <Paper sx={{ padding: "16px", boxShadow: "none", backgroundColor: "#f9f9fb" }}>
          {/* Top Row: Operator Selector and Clear All */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <Box
              onClick={() => {
                setFilterOperator(filterOperator === "is" ? "is not" : "is");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <Text variant={TextVariant.md} style={{ fontWeight: 700, color: COLORS.foregroundPrimary, fontSize: "20px" }}>
                {filterOperator}
              </Text>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M8 10.3 Q7.5 10 8 9.7 L11 9.7 Q12 9.5 13 9.7 L16 9.7 Q16.5 10 16 10.3 L12.2 14.5 Q12 14.7 11.8 14.5 L8 10.3 Z" fill={COLORS.foregroundPrimary} />
              </svg>
            </Box>
            <Button
              variant={ButtonVariants.light}
              onClick={() => {
                setFilters({});
                setSelectedFilterType(null);
                setFilterOperator("is");
                setFilterValue("");
                setFilterConfigAnchorEl(null);
              }}
              style={{
                color: "var(--color-foregroundPrimary, #454262)",
                border: "none",
                backgroundColor: "transparent",
                padding: "4px 8px",
                minWidth: "auto",
              }}
            >
              Clear all
            </Button>
          </Box>

          {/* Value Input Field */}
          <Box sx={{ marginBottom: "24px", width: "100%", boxSizing: "border-box", padding: 0, display: "block" }}>
            {selectedFilterType === "status" && (
              <Box sx={{ width: "100%", "& .MuiFormControl-root": { width: "100% !important", minWidth: "0 !important" }, "& > *": { width: "100% !important" } }}>
                <Select
                displayEmpty
                fullWidth
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value as string)}
                renderValue={(value) => {
                  if (!value) return <span style={{ color: COLORS.foregroundSecondary }}>Value</span>;
                  return value === "active" ? "Active" : "Inactive";
                }}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  "& .MuiFormControl-root": {
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiInputBase-root": {
                    backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiInputBase-input": {
                    width: "100% !important",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                      borderRadius: "8px",
                      boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)",
                      "& .MuiMenuItem-root": {
                        padding: "8px 16px",
                        width: "100%",
                        margin: 0,
                        "&:hover": {
                          backgroundColor: "var(--color-backgroundSecondary, #f0eff2)",
                        },
                      },
                    },
                  },
                  BackdropProps: {
                    style: {
                      backgroundColor: 'transparent',
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: 0,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              />
              </Box>
            )}
            {selectedFilterType === "boringLicense" && (
              <Box sx={{ width: "100%", "& .MuiFormControl-root": { width: "100% !important", minWidth: "0 !important" }, "& > *": { width: "100% !important" } }}>
                <Select
                displayEmpty
                fullWidth
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ]}
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value as string)}
                renderValue={(value) => {
                  if (!value) return <span style={{ color: COLORS.foregroundSecondary }}>Value</span>;
                  return value === "active" ? "Active" : "Inactive";
                }}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  "& .MuiFormControl-root": {
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiInputBase-root": {
                    backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                    width: "100% !important",
                    maxWidth: "100% !important",
                    minWidth: "0 !important",
                  },
                  "& .MuiInputBase-input": {
                    width: "100% !important",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                      borderRadius: "8px",
                      boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)",
                      "& .MuiMenuItem-root": {
                        padding: "8px 16px",
                        width: "100%",
                        margin: 0,
                        "&:hover": {
                          backgroundColor: "var(--color-backgroundSecondary, #f0eff2)",
                        },
                      },
                    },
                  },
                  BackdropProps: {
                    style: {
                      backgroundColor: 'transparent',
                    },
                  },
                  MenuListProps: {
                    sx: {
                      padding: 0,
                    },
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                }}
              />
              </Box>
            )}
            {selectedFilterType === "role" && (
              <Box sx={{ width: "100%", "& .MuiFormControl-root": { width: "100% !important", minWidth: "0 !important" }, "& > *": { width: "100% !important" } }}>
                <Select
                  displayEmpty
                  fullWidth
                  multiple
                  options={availableRoles.map((role) => ({ value: role, label: role }))}
                  value={filterValues}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilterValues(typeof value === 'string' ? value.split(',') : value as string[]);
                  }}
                  renderValue={(selected) => {
                    const selectedArr = selected as string[];
                    if (!selectedArr || selectedArr.length === 0) {
                      return <span style={{ color: COLORS.foregroundSecondary }}>Select profiles...</span>;
                    }
                    return selectedArr.join(', ');
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    "& .MuiFormControl-root": {
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiInputBase-input": {
                      width: "100% !important",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                        borderRadius: "8px",
                        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)",
                        maxHeight: "300px",
                        "& .MuiMenuItem-root": {
                          padding: "8px 16px",
                          width: "100%",
                          margin: 0,
                          "&:hover": {
                            backgroundColor: "var(--color-backgroundSecondary, #f0eff2)",
                          },
                        },
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                />
              </Box>
            )}
            {selectedFilterType === "team" && (
              <Box sx={{ width: "100%", "& .MuiFormControl-root": { width: "100% !important", minWidth: "0 !important" }, "& > *": { width: "100% !important" } }}>
                <Select
                  displayEmpty
                  fullWidth
                  multiple
                  options={availableTeams.map((team) => ({ value: team, label: team }))}
                  value={filterValues}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilterValues(typeof value === 'string' ? value.split(',') : value as string[]);
                  }}
                  renderValue={(selected) => {
                    const selectedArr = selected as string[];
                    if (!selectedArr || selectedArr.length === 0) {
                      return <span style={{ color: COLORS.foregroundSecondary }}>Select teams...</span>;
                    }
                    return selectedArr.join(', ');
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    "& .MuiFormControl-root": {
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiInputBase-root": {
                      backgroundColor: "var(--color-backgroundWhite, #ffffff) !important",
                      width: "100% !important",
                      maxWidth: "100% !important",
                      minWidth: "0 !important",
                    },
                    "& .MuiInputBase-input": {
                      width: "100% !important",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: "var(--color-backgroundWhite, #ffffff)",
                        borderRadius: "8px",
                        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15), 0px 2px 8px rgba(0, 0, 0, 0.1)",
                        maxHeight: "300px",
                        "& .MuiMenuItem-root": {
                          padding: "8px 16px",
                          width: "100%",
                          margin: 0,
                          "&:hover": {
                            backgroundColor: "var(--color-backgroundSecondary, #f0eff2)",
                          },
                        },
                      },
                    },
                    MenuListProps: {
                      sx: {
                        padding: 0,
                      },
                    },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Done Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => {
                if (selectedFilterType === "status") {
                  setFilters({
                    ...filters,
                    status: { operator: filterOperator, value: filterValue },
                  });
                } else if (selectedFilterType === "boringLicense") {
                  setFilters({
                    ...filters,
                    boringLicense: { operator: filterOperator, value: filterValue },
                  });
                } else if (selectedFilterType === "role") {
                  // Multi-select for roles
                  if (filterValues.length > 0) {
                    setFilters({
                      ...filters,
                      role: { operator: filterOperator, values: filterValues },
                    });
                  }
                } else if (selectedFilterType === "team") {
                  // Multi-select for teams
                  if (filterValues.length > 0) {
                    setFilters({
                      ...filters,
                      teams: { operator: filterOperator, values: filterValues },
                    });
                  }
                }
                setSelectedFilterType(null);
                setFilterOperator("is");
                setFilterValue("");
                setFilterValues([]);
                setFilterConfigAnchorEl(null);
              }}
              style={{
                backgroundColor: "var(--color-backgroundBrand, #e8eaf1)",
                color: COLORS.foregroundBrand,
                border: "none",
              }}
            >
              Done
            </Button>
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default Users;
